import type { LangDict } from "./types";

// Strings for tenant-only screens (Home, Browse, Payments, Requests) and
// the shared components (receipts, agreements, location capture, photo
// lightbox). Keep namespace keys unique across core.ts / owner.ts / tenant.ts
// since they're merged flat.
export const tenant: LangDict = {
  en: {
    tenantHome: {
      moveOutSettlement: "Move-out settlement",
      yourLastPlace: "Your last place",
      depositPaid: "Deposit paid",
      deductionsLabel: "Deductions",
      refundableDeposit: "Refundable deposit",
      exitedOn: "Exited {date}",
      noActiveTenancyLine1: "You don't have an active tenancy yet.",
      noActiveTenancyLine2: "Browse listings to find a place, or ask your owner to add you once you've moved in.",
      welcomeTitle: "Welcome to ToLetBook!",
      welcomeSub: "Let's find a property for you to rent.",
      browsePropertiesBtn: "Browse Properties",
      rentForMonth: "Rent for {month}",
      paidStatus: "Paid",
      dueStatus: "Due",
      overdueStatus: "Overdue",
      awaitingConfirmationStatus: "Awaiting confirmation",
      dueInDays: "Due in {count} days",
      dueTodayLabel: "Due today",
      overdueByDays: "{count} days overdue",
      reminderBanner: "🔔 Your owner sent a reminder about this payment",
      paidCheck: "✓ Paid",
      onDate: "on {date}",
      viaMode: "via {mode}",
      receiptBtn: "Receipt",
      payingBtn: "Paying…",
      payAmount: "Pay {amount}",
      paymentRecordedToast: "Payment recorded",
      paymentPendingConfirmationToast: "Marked as paid — waiting for your owner to confirm",
      pendingConfirmationNote: "You marked this as paid via {mode}. Waiting for your owner to confirm receipt.",
      ownerLabel: "Owner",
      callOwnerAria: "Call owner",
      upiIdLabel: "UPI ID",
      copyUpiAria: "Copy UPI ID",
      upiCopiedToast: "UPI ID copied",
      upiCopyFailedToast: "Couldn't copy UPI ID",
      raiseRequest: "Raise Request",
      viewLease: "View Lease",
      receiptsNav: "Receipts",
      browseRooms: "Browse Rooms",
      openRequestsHeading: "Open Requests",
      viewAll: "View all",
      noOpenRequests: "No open requests.",
      noDetailsProvided: "No details provided",
    },
    tenantBrowse: {
      occBoys: "Boys PG",
      occGirls: "Girls PG",
      occCoEd: "Co-ed PG",
      occDefault: "PG",
      filterAll: "All",
      filterPg: "PG",
      filter1RK: "1RK",
      filter1BHK: "1BHK",
      filter2BHK: "2BHK",
      filter3BHK: "3BHK",
      filter4BHK: "4BHK",
      filter5BHK: "5BHK",
      filter6BHK: "6BHK",
      filterDuplex: "Duplex",
      filterVilla: "Villa",
      filterShop: "Shop",
      filterOffice: "Office",
      filterShowroom: "Showroom",
      filterWarehouse: "Warehouse",
      filterCoworking: "Co-working",
      filterFactory: "Factory",
      sortDefault: "Default",
      sortPriceAsc: "Price: Low to High",
      sortPriceDesc: "Price: High to Low",
      sortDistance: "Distance: Nearest",
      locationNotSupported: "Location isn't supported in this browser",
      locationPermissionDenied: "Location permission denied",
      locationCouldNotGet: "Could not get your location",
      heading: "Let's find the ToLets for you",
      curatedHeading: "Discover a place",
      curatedTopPicksHeading: "★ Top picks near you",
      curatedBudgetHeading: "Budget-friendly, under {amount}",
      curatedPgHeading: "PG & co-living",
      curatedSeeAll: "See all",
      searchPlaceholder: "Search by locality or property name",
      searchLocationPlaceholder: "🔍 Search a locality, landmark or address",
      filtersBtn: "⚙ Filters",
      listView: "☰ List view",
      mapView: "🗺️ Map view",
      fromPrice: "from {price}",
      housesCount: "{count} houses",
      noVacancies: "No vacancies right now — check back soon.",
      noMatch: "No listings match your search or filter.",
      noPinnedLocation: "None of these listings have a pinned location yet.",
      distanceAway: "{value} away",
      filtersTitle: "Filters",
      propertyTypeLabel: "Property type",
      sortByLabel: "Sort by",
      budgetLabel: "Budget (₹ per month)",
      budgetAny: "Any",
      budgetUnder: "Under {amount}",
      budgetOver: "Over {amount}",
      budgetMinPlaceholder: "Min",
      budgetMaxPlaceholder: "Max",
      yourLocationLabel: "Your location",
      locating: "Locating…",
      locationOn: "📍 Location on",
      useMyLocation: "📍 Use my location",
      nearLabel: "📍 Near {location}",
      nearYou: "Near you",
      findingLocation: "Finding your location…",
      allowLocationPrompt: "Allow location to see homes near you",
      updateLocation: "Update",
      withinKm: "Within {km} km",
      allAreas: "All areas",
      noneNearby: "No properties within {km} km of you yet.",
      showAllProperties: "Show all properties",
      clearAll: "Clear all",
      showResults: "Show results",
      backToListings: "‹ Back to listings",
      housesHeading: "Houses",
      occupiedBadge: "Occupied",
      vacantBadge: "Vacant",
      typeLabel: "Type",
      rentLabel: "Rent",
      perMonthSuffix: "/mo",
      rentBenchmarkBelow: "At or below the {amount}/mo average for similar listings nearby",
      rentBenchmarkAbove: "Above the {amount}/mo average for similar listings nearby",
      depositLabel: "Deposit",
      escalationLabel: "Rent escalation",
      yearsSuffix: "yrs",
      occupancyLabel: "Occupancy",
      mealPlanLabel: "Meals included",
      preferredTenantsLabel: "Preferred tenants",
      roomAc: "AC",
      roomAttachedBathroom: "Attached bathroom",
      bedsVacant: "{vacant} of {total} beds vacant",
      locationLabel: "Location",
      openInGoogleMaps: "Open in Google Maps",
      viewOnMap: "View on map",
      hideMap: "Hide map",
      showNearby: "Show what's nearby",
      hideNearby: "Hide nearby places",
      loadingNearby: "Looking up nearby places…",
      nearbyEmpty: "No nearby places found",
      nearbyTransit: "Transit",
      nearbyEssentials: "Essentials",
      nearbyHealthcare: "Healthcare",
      metersAway: "{distance} m away",
      similarPropertiesHeading: "Similar properties",
      topTag: "Top",
      featuredTag: "Featured",
      moveInHint: "To move in, ask the owner to add you as a tenant with your registered phone number — it'll then show up under your Home tab automatically.",
      scheduleVisit: "Schedule a visit",
      pickDateTime: "Pick a date and time",
      visitRequestSent: "Visit request sent to the owner",
      couldNotSendVisit: "Could not send the visit request",
      requestedFor: "Requested for {date} at {time} — the owner has been notified and will confirm.",
      dateLabel: "Date",
      timeLabel: "Time",
      noteOptionalLabel: "Note (optional)",
      notePlaceholder: "e.g. Prefer evening, calling ahead",
      sending: "Sending…",
      requestVisitBtn: "Request visit",
      ownerContactLabel: "Owner",
      verifiedOwnerBadge: "Verified",
      trustedOwnerBadge: "Trusted (3+ referrals)",
      unlockingBtn: "Unlocking…",
      unlockNumberFreeBtn: "🔓 Unlock number — free",
      unlockUpsellTitle: "Free unlock used",
      unlockUpsellBody: "Basic plan includes 1 free owner contact unlock. Upgrade to Tenant Pro to unlock unlimited owners.",
      unlockUpsellBtn: "Upgrade to Tenant Pro",
      callNumberBtn: "Call {phone}",
      whatsappBtn: "💬 Chat on WhatsApp",
      whatsappMessage: "Hi, I'm interested in your property \"{property}\" listed on ToLetBook. Is it still available?",
      contactUnlockedToast: "Contact number unlocked",
      couldNotUnlockToast: "Could not unlock contact",
    },
    tenantPayments: {
      noActiveTenancy: "You don't have an active tenancy to pay rent for.",
      title: "Payments",
      paidStatus: "Paid",
      rentDue: "Rent due",
      pendingConfirmationStatus: "Awaiting owner confirmation",
      pendingConfirmationNote: "You marked this as paid via {mode}. Your receipt will be ready once the owner confirms receipt.",
      reminderBanner: "🔔 Your owner sent a reminder about this payment",
      payingBtn: "Paying…",
      payViaUpi: "Pay {amount} via UPI",
      payViaBank: "Pay {amount} via bank transfer",
      payNow: "Pay {amount}",
      paymentRecordedToast: "Payment recorded",
      paymentPendingConfirmationToast: "Marked as paid — waiting for owner to confirm",
      paymentFailedToast: "Payment didn't go through — try again",
      viewReceiptBtn: "View receipt",
      history: "History",
      pastRentalsHeading: "Past rentals",
      pastRentalUnknownProperty: "Previous property",
      pastRentalDateRange: "{start} – {end}",
      getCertificateBtn: "🧾 Get rent certificate (for HRA)",
      certificateTitle: "Rent Certificate",
      certificateEmpty: "No paid rent found for {year}",
      certificateDisclaimer: "A summary of rent paid, for your own HRA tax exemption filing — not a legally signed certificate.",
      certificateTenant: "Tenant",
      certificateOwner: "Owner",
      certificateProperty: "Property",
      certificateYear: "Financial year",
      certificateTotal: "Total paid",
      certificatePrintBtn: "Print / Save as PDF",
      payViaUpiDirectlyBtn: "Pay via UPI directly",
      ownerUpiIdLabel: "Owner's UPI ID",
      ownerBankDetailsLabel: "Owner's bank account",
      copyUpiBtn: "Copy",
      upiIdCopiedToast: "Copied",
      openUpiAppBtn: "Open UPI app",
      markPaidUpiBtn: "I've paid",
      markPaidNoDetailsBtn: "Mark {amount} as paid",
      markPaidUpiConfirm: "Have you already paid this rent directly to the owner? This marks it as paid.",
      chooseHowPaid: "How did you pay?",
      methodUpi: "UPI",
      methodBank: "Bank transfer",
      methodCash: "Cash",
      upiRefLabel: "UPI reference number (12 digits)",
      upiRefHint: "In your UPI app, open this payment and look for \"UPI Ref\" or \"UTR\". It lets your owner match the payment.",
      upiRefPlaceholder: "e.g. 412345678901",
      upiRefInvalid: "Enter the 12-digit reference, or choose \"I don't have it\".",
      noRefLink: "I don't have it",
      haveRefLink: "I'll enter it",
      bankRefLabel: "Transaction reference (optional)",
      cashInfo: "Pay your owner directly, then tell us here. Your owner will be asked to confirm they received it.",
      cashNoteLabel: "Note for your owner (optional)",
      cashNotePlaceholder: "e.g. gave it to the caretaker on the 3rd",
      markPaidCashBtn: "I paid {amount} in cash",
      markPaidCashConfirm: "Confirm that you paid this rent in cash. Your owner will be asked to confirm it.",
      pendingConfirmationCashNote: "You marked this as paid in cash. It will show as paid once your owner confirms.",
      referenceLine: "Reference: {ref}",
      rejectedBanner: "Your owner couldn't confirm your last payment: {reason}. Please check and try again.",
    },
    tenantSavedSearches: {
      title: "Saved Searches",
      hint: "Get notified the moment a new listing matches — no need to keep checking Browse.",
      lockedTitle: "Get notified the instant a match appears",
      proRequiredNote: "Saved-search alerts are a Tenant Pro feature.",
      upgradeBtn: "Upgrade to Pro",
      emptyState: "No saved searches yet.",
      addBtn: "+ Save a search",
      savedToast: "Search saved — we'll notify you about matching listings",
      anyCategory: "Any type",
      anyListing: "Any listing",
      underRent: "under {amount}",
      lastMatched: "Matched a listing recently",
      cityLabel: "City",
      cityPlaceholder: "e.g. Bengaluru",
      localityLabel: "Locality",
      localityPlaceholder: "e.g. Indiranagar",
      maxRentLabel: "Max rent",
      maxRentPlaceholder: "e.g. 20000",
    },
    tenantRequests: {
      title: "Maintenance Requests",
      cancelLink: "Cancel ›",
      newLink: "+ New ›",
      whatsWrong: "What's wrong?",
      issuePlaceholder: "e.g. Leaking tap",
      detailsOptional: "Details (optional)",
      submittingBtn: "Submitting…",
      submitRequestBtn: "Submit request",
      giveTitleError: "Give the request a title",
      requestRaisedToast: "Request raised",
      noRequestsYet: "No requests raised yet.",
      noDetailsProvided: "No details provided",
      repairsTab: "Repairs",
      visitsTab: "Visits",
      vacateTab: "Vacate",
      raiseRequestCta: "Raise a request",
      myVisitsHeading: "My Visit Requests",
      emptyNoVisits: "You haven't requested any property visits yet.",
      visitLine: "{date} at {time}",
      cancelVisitButton: "Cancel",
      visitCancelledToast: "Visit request cancelled",
      visitStatusPending: "Pending",
      visitStatusConfirmed: "Confirmed",
      visitStatusDeclined: "Declined",
      visitStatusCancelled: "Cancelled",
      vacateNoticeHeading: "Vacate Notice",
      giveNoticeLink: "+ Give notice ›",
      vacateOnLabel: "Vacating on {date}",
      noticeGivenOnLabel: "Notice given on {date}",
      vacateStatusPending: "Pending",
      vacateStatusAcknowledged: "Acknowledged",
      vacateStatusCancelled: "Cancelled",
      shareWhatsappBtn: "💬 Share on WhatsApp",
      cancelNoticeBtn: "Cancel notice",
      noticeDateLabel: "Notice date",
      vacateDatePreview: "Based on your {days}-day notice period, you'll be vacating on {date}",
      submitVacateNoticeBtn: "Submit vacate notice",
      vacateNoticeSentToast: "Vacate notice sent to the owner",
      vacateNoticeCancelledToast: "Vacate notice cancelled",
      emptyNoVacateNotices: "You haven't given any vacate notice.",
      vacateWhatsappMessage: "Hi {owner}, this is to formally give notice that I'll be vacating \"{property}\". Notice given on {noticeDate}, vacating on {vacateDate}.",
    },
    tenantOnboarding: {
      title: "Complete Your Move-In",
      nameLabel: "Your name (as per Aadhaar)",
      nameHint: "This exact name will be used on your rental agreement.",
      rentLabel: "Monthly rent",
      dueDayLabel: "Rent due day",
      dueDayValue: "Day {day} of every month",
      depositLabel: "Security deposit",
      depositPaidBadge: "Paid",
      depositPendingBadge: "Pending",
      depositConfirmingBadge: "Confirming…",
      payNowBtn: "Pay now",
      payingBtn: "Opening payment…",
      depositPaymentDesc: "Security deposit",
      depositPaidToast: "Deposit payment received",
      depositFailedToast: "Payment failed — try again",
      depositPaidCashBadge: "Paid in cash",
      paidInCashLink: "I paid in cash instead",
      cashProofHint: "Add a photo of the receipt or payment proof (optional).",
      cashProofUploadLabel: "Add proof",
      confirmCashBtn: "Confirm cash payment",
      errDepositRequired: "Pay the security deposit (online or in cash) before continuing",
      amenitiesLabel: "Amenities provided by owner",
      identityVerificationLabel: "Identity Verification",
      yourIdentityLabel: "You",
      identityVerificationHint: "Verify your identity with DigiLocker so your government-matched name appears on the rental agreement.",
      confirmLabel: "I've reviewed my move-in details above and confirm they're correct.",
      submitBtn: "Complete setup",
      errNameRequired: "Enter your name",
      errMustConfirm: "Please confirm you've reviewed the details",
      completedToast: "Move-in details confirmed",
    },
    moveInDetails: {
      title: "Move-In Details",
    },
    locationCapture: {
      pinLocationLabel: "Pin this property's location",
      panMapHint: "📌 Move the map to place the pin exactly",
      expandMapAria: "Expand map",
      clear: "Clear",
      searchPlaceholder: "Search for an address",
      loadingSearch: "Loading search…",
      or: "or",
      locating: "Locating…",
      useCurrentLocation: "📍 Use current location",
      pickSuggestion: "Pick a suggestion from the dropdown",
      locationNotSupported: "Location isn't supported in this browser",
      locationPermissionDenied: "Location permission denied",
      locationCouldNotGet: "Could not get your location",
      hint: "Search an address or tap GPS to auto-fill the locality below — you'll still enter the door number and building name yourself.",
    },
    googleMap: {
      yourLocation: "Your location",
      searchedLocation: "Searched location",
      couldNotLoad: "Could not load Google Maps — check the API key",
      viewDetails: "View details ›",
    },
    rentReceipt: {
      back: "‹ Back",
      paymentRecordedBanner: "✓ Payment recorded — here's your receipt.",
      title: "Rent Receipt",
      amountPaid: "Amount paid",
      receiptNoLabel: "Receipt No",
      paidByLabel: "Paid by",
      propertyLabel: "Property",
      receivedByLabel: "Received by",
      forMonthLabel: "For the month of",
      paymentModeLabel: "Payment mode",
      paidOnLabel: "Paid on",
      downloadBtn: "Download",
      shareBtn: "Share",
      htmlTitle: "Rent Receipt - {month}",
      htmlStamp: "✓ PAID",
      htmlAmountPaidLabel: "Amount Paid",
      htmlFooter: "This is a system-generated receipt from ToLetBook. Rent shown here is recorded within the app and not processed through a real payment gateway.",
      shareTitle: "Rent receipt - {month}",
      shareText: "Receipt {receiptNo} for {amount}",
    },
    rentalAgreement: {
      back: "‹ Back",
      title: "Rental Agreement",
      ownerLabel: "Owner",
      tenantLabel: "Tenant",
      propertyLabel: "Property",
      typeLabel: "Type",
      monthlyRentLabel: "Monthly rent",
      securityDepositLabel: "Security deposit",
      moveInDateLabel: "Move-in date",
      disclaimer: "This is an auto-generated summary from the tenancy details on record, not a legally binding document. Standard demo terms: rent due by the 5th each month, 30 days' notice to end the tenancy, deposit refundable less damages beyond normal wear and tear.",
      downloadAgreementBtn: "Download agreement",
      stampStatusLabel: "E-Stamp status",
      stampStatusNotStarted: "Not started",
      stampStatusProcessing: "Processing",
      stampStatusVerified: "Verified",
      stampHint: "Get this agreement legally e-stamped by the government. A real government fee of {amount} applies.",
      stampFeeAlreadyPaidHint: "Your e-stamp fee is paid — we just couldn't place the order yet. Retry below at no extra charge.",
      sendForStampBtn: "Pay {amount} & Send for E-Stamp",
      retrySendForStampBtn: "Retry sending for E-Stamp",
      sendingStampBtn: "Sending…",
      confirmingPaymentBtn: "Confirming payment…",
      stampFeePaymentDesc: "Government e-stamp fee",
      stampFeeFailedToast: "Payment failed — try again",
      stampSentToast: "Sent for government e-stamping",
      freeEstampToast: "Sent for government e-stamping — free with Premium this month",
      refreshStatusBtn: "Refresh status",
      viewStampedBtn: "View Stamped Agreement",
      identityVerificationLabel: "Identity Verification",
      digilockerVerifiedBadge: "Verified",
      digilockerNotVerifiedBadge: "Not verified",
      verifyDigilockerBtn: "Verify with DigiLocker",
      verifyingDigilockerBtn: "Opening DigiLocker…",
      digilockerVerifiedToast: "Verified as {name}",
      digilockerDeniedToast: "DigiLocker verification was cancelled",
      digilockerExpiredToast: "That DigiLocker link expired — try again",
      comingSoonTitle: "Government e-stamping is coming soon",
      comingSoonBody: "We're getting stamp duty payments set up so you can generate a fully government-verified, e-stamped rental agreement right from the app.",
      interestBtn: "I'm interested — notify me",
      interestRegisteredBtn: "You're on the list ✓",
      interestToast: "Thanks! We'll notify you when e-stamping is live.",
      uploadDocHeading: "Already have a rental agreement?",
      uploadDocHint: "If you've already made one outside ToLetBook, upload it here so it's saved with this tenancy.",
      uploadDocBtn: "Upload rental agreement",
      viewUploadedDocBtn: "View uploaded agreement",
      removeUploadedDocBtn: "Remove",
      uploadedOnLabel: "Uploaded on {date}",
      noUploadedDocTenantHint: "Your owner hasn't uploaded a rental agreement yet.",
      uploadDocSuccessToast: "Rental agreement uploaded",
      uploadDocFailedToast: "Could not upload — try a PDF or image under 8MB",
      removeDocConfirm: "Remove the uploaded rental agreement?",
      removeDocSuccessToast: "Rental agreement removed",
      htmlTitle: "Rental Agreement - {property}",
      htmlDocTitle: "Rental Agreement Summary",
      htmlAgreementNo: "Agreement No. {no}",
      htmlOwnerLessor: "Owner / Lessor",
      htmlTenantLessee: "Tenant / Lessee",
      htmlPropertyHeading: "Property",
      htmlAddressLabel: "Address",
      htmlTypeLabel: "Type",
      htmlTermsHeading: "Terms",
      htmlMonthlyRentLabel: "Monthly rent",
      htmlSecurityDepositLabel: "Security deposit",
      htmlMoveInDateLabel: "Move-in date",
      htmlConditionsHeading: "Conditions",
      htmlCond1: "Rent is due on or before the 5th of every month, payable to the owner via the agreed mode.",
      htmlCond2: "The security deposit is refundable at move-out, less any deductions for damages beyond normal wear and tear.",
      htmlCond3: "Either party must give at least 30 days' written notice before ending the tenancy.",
      htmlCond4: "The tenant is responsible for utility bills unless otherwise agreed with the owner.",
      htmlCond5: "Maintenance of fixtures and fittings is the owner's responsibility unless damage is caused by tenant negligence.",
      htmlFooter: "This summary is auto-generated by ToLetBook from the tenancy details on record and does not constitute a legally binding document. Consult a legal professional to draft a formal rental agreement.",
    },
    photoLightbox: {
      closeAria: "Close",
      previousPhotoAria: "Previous photo",
      nextPhotoAria: "Next photo",
      countLabel: "{current} / {total}",
    },
    tenantPlan: {
      title: "Plan",
      currentPlanLabel: "Current plan",
      basicName: "Basic",
      proName: "Tenant Pro",
      proSub: "For tenants actively house-hunting",
      priceValidity: "valid 1 month",
      featUnlock: "Unlock unlimited owner contact numbers for 30 days",
      featNearby: "Full map and nearby-places access on every listing",
      featWhatsapp: "WhatsApp the owner directly, no per-listing fee",
      featSavedSearch: "Save up to 5 searches — get notified the instant a matching listing goes up",
      featFallback: "Without subscribing, you still get 1 free owner contact unlock — upgrade for more",
      currentPlanBtn: "Current plan",
      subscribeBtn: "Subscribe for ₹99",
      activeUntil: "Active until {date}",
      trialBadge: "Free trial",
      trialEndsOn: "Free until {date}, then ₹99/month",
      noChargeNote: "No payment required yet — subscribing is free while billing is being set up.",
      subscribedToast: "Tenant Pro activated",
      switchFailedToast: "Could not update your plan",
      nearbyUpsellTitle: "Unlock map and nearby places",
      nearbyUpsellBody: "Subscribe to Tenant Pro to see transit, essentials and healthcare near every listing.",
      nearbyUpsellBtn: "See Tenant Pro",
      cancelPlanBtn: "Cancel plan",
      securePaymentNote: "Payments are processed securely via Razorpay.",
      cancelledToast: "Tenant Pro cancelled",
    },
  },
  hi: {
    tenantHome: {
      moveOutSettlement: "मूव-आउट सेटलमेंट",
      yourLastPlace: "आपकी पिछली जगह",
      depositPaid: "जमा किया गया डिपॉज़िट",
      deductionsLabel: "कटौती",
      refundableDeposit: "वापसी योग्य डिपॉज़िट",
      exitedOn: "{date} को छोड़ा",
      noActiveTenancyLine1: "अभी आपकी कोई एक्टिव टेनेंसी नहीं है।",
      noActiveTenancyLine2: "रहने की जगह ढूंढने के लिए लिस्टिंग देखें, या शिफ्ट होने के बाद अपने मालिक से आपको जोड़ने को कहें।",
      welcomeTitle: "ToLetBook में आपका स्वागत है!",
      welcomeSub: "आपके लिए किराए की प्रॉपर्टी ढूंढते हैं।",
      browsePropertiesBtn: "प्रॉपर्टी देखें",
      rentForMonth: "{month} का किराया",
      paidStatus: "भुगतान हो गया",
      dueStatus: "बकाया",
      overdueStatus: "ओवरड्यू",
      dueInDays: "{count} दिनों में देय",
      dueTodayLabel: "आज देय है",
      overdueByDays: "{count} दिन ओवरड्यू",
      reminderBanner: "🔔 आपके मालिक ने इस भुगतान के बारे में रिमाइंडर भेजा है",
      paidCheck: "✓ भुगतान हो गया",
      onDate: "{date} को",
      viaMode: "{mode} के ज़रिए",
      receiptBtn: "रसीद",
      payingBtn: "भुगतान हो रहा है…",
      payAmount: "{amount} भरें",
      paymentRecordedToast: "भुगतान दर्ज हो गया",
      ownerLabel: "मालिक",
      callOwnerAria: "मालिक को कॉल करें",
      raiseRequest: "रिक्वेस्ट भेजें",
      viewLease: "एग्रीमेंट देखें",
      receiptsNav: "रसीदें",
      browseRooms: "कमरे देखें",
      openRequestsHeading: "खुली रिक्वेस्ट",
      viewAll: "सभी देखें",
      noOpenRequests: "कोई खुली रिक्वेस्ट नहीं है।",
      noDetailsProvided: "कोई विवरण नहीं दिया गया",
    },
    tenantBrowse: {
      occBoys: "बॉयज़ पीजी",
      occGirls: "गर्ल्स पीजी",
      occCoEd: "को-एड पीजी",
      occDefault: "पीजी",
      filterAll: "सभी",
      filterPg: "पीजी",
      filter1RK: "1RK",
      filter1BHK: "1BHK",
      filter2BHK: "2BHK",
      filter3BHK: "3BHK",
      filterVilla: "विला",
      filterShop: "दुकान",
      filterOffice: "ऑफिस",
      filterShowroom: "शोरूम",
      filterWarehouse: "गोदाम",
      filterCoworking: "को-वर्किंग",
      filterFactory: "फैक्ट्री",
      sortDefault: "डिफ़ॉल्ट",
      sortPriceAsc: "कीमत: कम से ज़्यादा",
      sortPriceDesc: "कीमत: ज़्यादा से कम",
      sortDistance: "दूरी: सबसे नज़दीक",
      locationNotSupported: "इस ब्राउज़र में लोकेशन सपोर्ट नहीं है",
      locationPermissionDenied: "लोकेशन की अनुमति नहीं मिली",
      locationCouldNotGet: "आपकी लोकेशन नहीं मिल पाई",
      heading: "आपके लिए किराए की जगह ढूंढते हैं",
      searchPlaceholder: "इलाका या प्रॉपर्टी के नाम से खोजें",
      filtersBtn: "⚙ फ़िल्टर",
      listView: "☰ लिस्ट व्यू",
      mapView: "🗺️ मैप व्यू",
      fromPrice: "{price} से शुरू",
      housesCount: "{count} घर",
      noVacancies: "अभी कोई खाली जगह नहीं है — बाद में देखें।",
      noMatch: "आपकी खोज या फ़िल्टर से कोई लिस्टिंग नहीं मिली।",
      noPinnedLocation: "इनमें से किसी भी लिस्टिंग में लोकेशन पिन नहीं है।",
      distanceAway: "{value} दूर",
      filtersTitle: "फ़िल्टर",
      propertyTypeLabel: "प्रॉपर्टी टाइप",
      sortByLabel: "इस हिसाब से क्रम करें",
      yourLocationLabel: "आपकी लोकेशन",
      locating: "लोकेशन ढूंढ रहे हैं…",
      locationOn: "📍 लोकेशन ऑन है",
      useMyLocation: "📍 मेरी लोकेशन इस्तेमाल करें",
      nearLabel: "📍 {location} के पास",
      nearYou: "आपके पास",
      findingLocation: "आपकी लोकेशन ढूंढ रहे हैं…",
      allowLocationPrompt: "पास के घर देखने के लिए लोकेशन की अनुमति दें",
      updateLocation: "अपडेट करें",
      withinKm: "{km} किमी के अंदर",
      allAreas: "सभी इलाके",
      noneNearby: "आपके {km} किमी के अंदर अभी कोई प्रॉपर्टी नहीं है।",
      showAllProperties: "सभी प्रॉपर्टी दिखाएं",
      clearAll: "सब हटाएं",
      showResults: "नतीजे दिखाएं",
      backToListings: "‹ लिस्टिंग पर वापस जाएं",
      housesHeading: "घर",
      occupiedBadge: "भरा हुआ",
      vacantBadge: "खाली",
      typeLabel: "टाइप",
      rentLabel: "किराया",
      perMonthSuffix: "/माह",
      depositLabel: "डिपॉज़िट",
      escalationLabel: "किराया वृद्धि",
      yearsSuffix: "साल",
      occupancyLabel: "ऑक्युपेंसी",
      mealPlanLabel: "भोजन शामिल",
      preferredTenantsLabel: "पसंदीदा किराएदार",
      roomAc: "एसी",
      roomAttachedBathroom: "अटैच्ड बाथरूम",
      bedsVacant: "{total} में से {vacant} बेड खाली",
      locationLabel: "लोकेशन",
      openInGoogleMaps: "Google Maps में खोलें",
      viewOnMap: "मैप पर देखें",
      hideMap: "मैप छिपाएं",
      showNearby: "आस-पास क्या है, देखें",
      hideNearby: "आस-पास की जगहें छिपाएं",
      loadingNearby: "आस-पास की जगहें खोजी जा रही हैं…",
      nearbyEmpty: "आस-पास कोई जगह नहीं मिली",
      nearbyTransit: "ट्रांज़िट",
      nearbyEssentials: "ज़रूरी सुविधाएं",
      nearbyHealthcare: "स्वास्थ्य सेवा",
      metersAway: "{distance} मीटर दूर",
      similarPropertiesHeading: "मिलती-जुलती प्रॉपर्टी",
      topTag: "टॉप",
      featuredTag: "फीचर्ड",
      moveInHint: "शिफ्ट होने के लिए, मालिक से अपने रजिस्टर्ड फोन नंबर के साथ आपको किराएदार के रूप में जोड़ने को कहें — फिर यह आपके होम टैब में अपने आप दिखने लगेगा।",
      scheduleVisit: "विज़िट शेड्यूल करें",
      pickDateTime: "तारीख और समय चुनें",
      visitRequestSent: "विज़िट रिक्वेस्ट मालिक को भेज दी गई",
      couldNotSendVisit: "विज़िट रिक्वेस्ट नहीं भेजी जा सकी",
      requestedFor: "{date} को {time} बजे के लिए रिक्वेस्ट भेजी गई — मालिक को सूचित कर दिया गया है, वे पुष्टि करेंगे।",
      dateLabel: "तारीख",
      timeLabel: "समय",
      noteOptionalLabel: "नोट (वैकल्पिक)",
      notePlaceholder: "जैसे शाम को आना पसंद है, पहले कॉल करूंगा",
      sending: "भेजा जा रहा है…",
      requestVisitBtn: "विज़िट रिक्वेस्ट करें",
      ownerContactLabel: "मालिक",
      verifiedOwnerBadge: "सत्यापित",
      unlockingBtn: "अनलॉक हो रहा है…",
      unlockNumberFreeBtn: "🔓 नंबर अनलॉक करें — मुफ़्त",
      unlockUpsellTitle: "मुफ़्त अनलॉक इस्तेमाल हो गया",
      unlockUpsellBody: "बेसिक प्लान में 1 मालिक का कॉन्टैक्ट मुफ़्त में अनलॉक होता है। असीमित मालिकों को अनलॉक करने के लिए Tenant Pro में अपग्रेड करें।",
      unlockUpsellBtn: "Tenant Pro में अपग्रेड करें",
      callNumberBtn: "{phone} पर कॉल करें",
      whatsappBtn: "💬 WhatsApp पर चैट करें",
      whatsappMessage: "नमस्ते, मुझे ToLetBook पर लिस्ट की गई आपकी प्रॉपर्टी \"{property}\" में दिलचस्पी है। क्या यह अभी भी उपलब्ध है?",
      contactUnlockedToast: "कॉन्टैक्ट नंबर अनलॉक हो गया",
      couldNotUnlockToast: "कॉन्टैक्ट अनलॉक नहीं हो सका",
    },
    tenantPayments: {
      noActiveTenancy: "आपकी कोई एक्टिव टेनेंसी नहीं है जिसका किराया भरा जाए।",
      title: "भुगतान",
      paidStatus: "भुगतान हो गया",
      rentDue: "किराया बकाया है",
      reminderBanner: "🔔 आपके मालिक ने इस भुगतान के बारे में रिमाइंडर भेजा है",
      payingBtn: "भुगतान हो रहा है…",
      payViaUpi: "UPI से {amount} भरें",
      payNow: "{amount} भरें",
      paymentFailedToast: "भुगतान नहीं हो पाया — फिर से कोशिश करें",
      paymentRecordedToast: "भुगतान दर्ज हो गया",
      viewReceiptBtn: "रसीद देखें",
      history: "इतिहास",
      payViaUpiDirectlyBtn: "सीधे UPI से भुगतान करें",
      ownerUpiIdLabel: "मालिक की UPI ID",
      copyUpiBtn: "कॉपी करें",
      upiIdCopiedToast: "UPI ID कॉपी हो गई",
      openUpiAppBtn: "UPI ऐप खोलें",
      markPaidUpiBtn: "मैंने UPI से भुगतान कर दिया है",
      markPaidUpiConfirm: "क्या आपने यह किराया मालिक की UPI ID पर भेज दिया है? इससे यह भुगतान हो गया के रूप में दर्ज होगा।",
    },
    tenantRequests: {
      title: "मेंटेनेंस रिक्वेस्ट",
      cancelLink: "रद्द करें ›",
      newLink: "+ नई ›",
      whatsWrong: "क्या समस्या है?",
      issuePlaceholder: "जैसे नल से पानी टपकना",
      detailsOptional: "विवरण (वैकल्पिक)",
      submittingBtn: "भेजा जा रहा है…",
      submitRequestBtn: "रिक्वेस्ट भेजें",
      giveTitleError: "रिक्वेस्ट का शीर्षक डालें",
      requestRaisedToast: "रिक्वेस्ट भेज दी गई",
      noRequestsYet: "अभी तक कोई रिक्वेस्ट नहीं भेजी गई।",
      noDetailsProvided: "कोई विवरण नहीं दिया गया",
      myVisitsHeading: "मेरी विज़िट रिक्वेस्ट",
      emptyNoVisits: "आपने अभी तक कोई प्रॉपर्टी विज़िट रिक्वेस्ट नहीं भेजी है।",
      visitLine: "{date} को {time} बजे",
      cancelVisitButton: "रद्द करें",
      visitCancelledToast: "विज़िट रिक्वेस्ट रद्द कर दी गई",
      visitStatusPending: "लंबित",
      visitStatusConfirmed: "कन्फर्म",
      visitStatusDeclined: "अस्वीकृत",
      visitStatusCancelled: "रद्द",
      vacateNoticeHeading: "वेकेट नोटिस",
      giveNoticeLink: "+ नोटिस दें ›",
      vacateOnLabel: "{date} को खाली किया जाएगा",
      noticeGivenOnLabel: "{date} को नोटिस दिया गया",
      vacateStatusPending: "लंबित",
      vacateStatusAcknowledged: "स्वीकृत",
      vacateStatusCancelled: "रद्द",
      shareWhatsappBtn: "💬 WhatsApp पर शेयर करें",
      cancelNoticeBtn: "नोटिस रद्द करें",
      noticeDateLabel: "नोटिस की तारीख",
      vacateDatePreview: "आपके {days}-दिन के नोटिस पीरियड के अनुसार, आप {date} को खाली करेंगे",
      submitVacateNoticeBtn: "वेकेट नोटिस भेजें",
      vacateNoticeSentToast: "वेकेट नोटिस मालिक को भेज दिया गया",
      vacateNoticeCancelledToast: "वेकेट नोटिस रद्द कर दिया गया",
      emptyNoVacateNotices: "आपने अभी तक कोई वेकेट नोटिस नहीं दिया है।",
      vacateWhatsappMessage: "नमस्ते {owner}, यह औपचारिक सूचना है कि \"{property}\" को {vacateDate} को खाली किया जाएगा। नोटिस {noticeDate} को दिया गया।",
    },
    tenantOnboarding: {
      title: "अपना मूव-इन पूरा करें",
      nameLabel: "आपका नाम (आधार के अनुसार)",
      nameHint: "यही सही नाम आपके रेंटल एग्रीमेंट पर इस्तेमाल किया जाएगा।",
      rentLabel: "मासिक किराया",
      dueDayLabel: "किराया देय दिन",
      dueDayValue: "हर महीने की {day} तारीख",
      depositLabel: "सिक्योरिटी डिपॉज़िट",
      depositPaidBadge: "भुगतान हो गया",
      depositPendingBadge: "बकाया",
      depositConfirmingBadge: "पुष्टि हो रही है…",
      payNowBtn: "अभी भुगतान करें",
      payingBtn: "भुगतान खुल रहा है…",
      depositPaymentDesc: "सिक्योरिटी डिपॉज़िट",
      depositPaidToast: "डिपॉज़िट भुगतान मिल गया",
      depositFailedToast: "भुगतान नहीं हो पाया — फिर से कोशिश करें",
      depositPaidCashBadge: "नकद में भुगतान",
      paidInCashLink: "मैंने नकद भुगतान किया है",
      cashProofHint: "रसीद या भुगतान का सबूत फोटो जोड़ें (वैकल्पिक)।",
      cashProofUploadLabel: "सबूत जोड़ें",
      confirmCashBtn: "नकद भुगतान की पुष्टि करें",
      errDepositRequired: "आगे बढ़ने से पहले सिक्योरिटी डिपॉज़िट भरें (ऑनलाइन या नकद)",
      amenitiesLabel: "मालिक द्वारा दी गई सुविधाएं",
      identityVerificationLabel: "पहचान सत्यापन",
      yourIdentityLabel: "आप",
      identityVerificationHint: "DigiLocker से अपनी पहचान सत्यापित करें ताकि आपका सरकारी-सत्यापित नाम रेंटल एग्रीमेंट पर दिखे।",
      confirmLabel: "मैंने ऊपर दी गई अपनी मूव-इन जानकारी देख ली है और पुष्टि करता/करती हूं कि यह सही है।",
      submitBtn: "सेटअप पूरा करें",
      errNameRequired: "अपना नाम डालें",
      errMustConfirm: "कृपया पुष्टि करें कि आपने जानकारी देख ली है",
      completedToast: "मूव-इन जानकारी की पुष्टि हो गई",
    },
    moveInDetails: {
      title: "मूव-इन जानकारी",
    },
    locationCapture: {
      pinLocationLabel: "इस प्रॉपर्टी की लोकेशन पिन करें",
      panMapHint: "📌 सही जगह पर पिन रखने के लिए मैप को घुमाएं",
      expandMapAria: "मैप बड़ा करें",
      clear: "हटाएं",
      searchPlaceholder: "पता खोजें",
      loadingSearch: "सर्च लोड हो रहा है…",
      or: "या",
      locating: "लोकेशन ढूंढ रहे हैं…",
      useCurrentLocation: "📍 मौजूदा लोकेशन इस्तेमाल करें",
      pickSuggestion: "ड्रॉपडाउन से कोई सुझाव चुनें",
      locationNotSupported: "इस ब्राउज़र में लोकेशन सपोर्ट नहीं है",
      locationPermissionDenied: "लोकेशन की अनुमति नहीं मिली",
      locationCouldNotGet: "आपकी लोकेशन नहीं मिल पाई",
      hint: "पता खोजें या नीचे इलाका अपने आप भरने के लिए GPS पर टैप करें — दरवाज़ा नंबर और बिल्डिंग का नाम आपको खुद डालना होगा।",
    },
    googleMap: {
      yourLocation: "आपकी लोकेशन",
      couldNotLoad: "Google Maps लोड नहीं हो सका — API की जांचें",
      viewDetails: "विवरण देखें ›",
    },
    rentReceipt: {
      back: "‹ वापस",
      paymentRecordedBanner: "✓ भुगतान दर्ज हो गया — यह रही आपकी रसीद।",
      title: "किराया रसीद",
      amountPaid: "भुगतान की गई राशि",
      receiptNoLabel: "रसीद नंबर",
      paidByLabel: "भुगतानकर्ता",
      propertyLabel: "प्रॉपर्टी",
      receivedByLabel: "प्राप्तकर्ता",
      forMonthLabel: "किस महीने के लिए",
      paymentModeLabel: "भुगतान का तरीका",
      paidOnLabel: "भुगतान की तारीख",
      downloadBtn: "डाउनलोड करें",
      shareBtn: "शेयर करें",
      htmlTitle: "किराया रसीद - {month}",
      htmlStamp: "✓ भुगतान हो गया",
      htmlAmountPaidLabel: "भुगतान की गई राशि",
      htmlFooter: "यह ToLetBook द्वारा सिस्टम-जनरेटेड रसीद है। यहां दिखाया गया किराया ऐप के अंदर दर्ज किया गया है और किसी असली पेमेंट गेटवे से प्रोसेस नहीं हुआ है।",
      shareTitle: "किराया रसीद - {month}",
      shareText: "रसीद {receiptNo}, राशि {amount}",
    },
    rentalAgreement: {
      back: "‹ वापस",
      title: "रेंटल एग्रीमेंट",
      ownerLabel: "मालिक",
      tenantLabel: "किराएदार",
      propertyLabel: "प्रॉपर्टी",
      typeLabel: "टाइप",
      monthlyRentLabel: "मासिक किराया",
      securityDepositLabel: "सिक्योरिटी डिपॉज़िट",
      moveInDateLabel: "शिफ्टिंग की तारीख",
      disclaimer: "यह रिकॉर्ड में मौजूद टेनेंसी जानकारी से अपने आप बना सारांश है, कोई कानूनी रूप से बाध्यकारी दस्तावेज़ नहीं है। डेमो की सामान्य शर्तें: किराया हर महीने की 5 तारीख तक देना है, टेनेंसी खत्म करने के लिए 30 दिन की सूचना, डिपॉज़िट सामान्य टूट-फूट से अधिक नुकसान की कटौती के बाद वापस मिलेगा।",
      downloadAgreementBtn: "एग्रीमेंट डाउनलोड करें",
      stampStatusLabel: "ई-स्टाम्प स्टेटस",
      stampStatusNotStarted: "शुरू नहीं हुआ",
      stampStatusProcessing: "प्रोसेसिंग हो रही है",
      stampStatusVerified: "वेरिफाइड",
      stampHint: "इस एग्रीमेंट को सरकार से कानूनी रूप से ई-स्टाम्प करवाएं। इसके लिए {amount} की असली सरकारी फीस लगेगी।",
      stampFeeAlreadyPaidHint: "आपकी ई-स्टाम्प फीस भर दी गई है — बस ऑर्डर अभी तक नहीं भेजा जा सका। बिना किसी अतिरिक्त शुल्क के नीचे फिर से कोशिश करें।",
      sendForStampBtn: "{amount} भरें और ई-स्टाम्प के लिए भेजें",
      retrySendForStampBtn: "ई-स्टाम्प के लिए फिर से भेजें",
      sendingStampBtn: "भेजा जा रहा है…",
      confirmingPaymentBtn: "भुगतान की पुष्टि हो रही है…",
      stampFeePaymentDesc: "सरकारी ई-स्टाम्प फीस",
      stampFeeFailedToast: "भुगतान नहीं हो पाया — फिर से कोशिश करें",
      stampSentToast: "सरकारी ई-स्टाम्पिंग के लिए भेज दिया गया",
      refreshStatusBtn: "स्टेटस रिफ्रेश करें",
      viewStampedBtn: "स्टाम्प्ड एग्रीमेंट देखें",
      identityVerificationLabel: "पहचान सत्यापन",
      digilockerVerifiedBadge: "सत्यापित",
      digilockerNotVerifiedBadge: "सत्यापित नहीं",
      verifyDigilockerBtn: "DigiLocker से सत्यापित करें",
      verifyingDigilockerBtn: "DigiLocker खुल रहा है…",
      digilockerVerifiedToast: "{name} के रूप में सत्यापित",
      digilockerDeniedToast: "DigiLocker सत्यापन रद्द कर दिया गया",
      digilockerExpiredToast: "वह DigiLocker लिंक समाप्त हो गया — फिर से कोशिश करें",
      comingSoonTitle: "सरकारी ई-स्टाम्पिंग जल्द आ रही है",
      comingSoonBody: "हम स्टाम्प ड्यूटी पेमेंट सेटअप कर रहे हैं ताकि आप ऐप से ही पूरी तरह सरकार-सत्यापित, ई-स्टाम्प्ड रेंटल एग्रीमेंट बना सकें।",
      interestBtn: "मुझे दिलचस्पी है — सूचित करें",
      interestRegisteredBtn: "आप लिस्ट में हैं ✓",
      interestToast: "धन्यवाद! ई-स्टाम्पिंग लाइव होने पर हम आपको सूचित करेंगे।",
      uploadDocHeading: "पहले से रेंटल एग्रीमेंट है?",
      uploadDocHint: "अगर आपने ToLetBook के बाहर पहले ही एक बना लिया है, तो इसे यहां अपलोड करें ताकि यह इस किराएदारी के साथ सेव हो जाए।",
      uploadDocBtn: "रेंटल एग्रीमेंट अपलोड करें",
      viewUploadedDocBtn: "अपलोड किया एग्रीमेंट देखें",
      removeUploadedDocBtn: "हटाएं",
      uploadedOnLabel: "{date} को अपलोड किया गया",
      noUploadedDocTenantHint: "आपके मकान मालिक ने अभी तक रेंटल एग्रीमेंट अपलोड नहीं किया है।",
      uploadDocSuccessToast: "रेंटल एग्रीमेंट अपलोड हो गया",
      uploadDocFailedToast: "अपलोड नहीं हो सका — 8MB से कम की PDF या इमेज आज़माएं",
      removeDocConfirm: "अपलोड किया गया रेंटल एग्रीमेंट हटाएं?",
      removeDocSuccessToast: "रेंटल एग्रीमेंट हटा दिया गया",
      htmlTitle: "रेंटल एग्रीमेंट - {property}",
      htmlDocTitle: "रेंटल एग्रीमेंट सारांश",
      htmlAgreementNo: "एग्रीमेंट नंबर {no}",
      htmlOwnerLessor: "मालिक / लेसर",
      htmlTenantLessee: "किराएदार / लेसी",
      htmlPropertyHeading: "प्रॉपर्टी",
      htmlAddressLabel: "पता",
      htmlTypeLabel: "टाइप",
      htmlTermsHeading: "शर्तें",
      htmlMonthlyRentLabel: "मासिक किराया",
      htmlSecurityDepositLabel: "सिक्योरिटी डिपॉज़िट",
      htmlMoveInDateLabel: "शिफ्टिंग की तारीख",
      htmlConditionsHeading: "नियम",
      htmlCond1: "किराया हर महीने की 5 तारीख तक, तय किए गए तरीके से मालिक को देना है।",
      htmlCond2: "सामान्य टूट-फूट से अधिक नुकसान की कटौती के बाद, शिफ्ट होते समय सिक्योरिटी डिपॉज़िट वापस मिलेगा।",
      htmlCond3: "टेनेंसी खत्म करने से पहले दोनों में से किसी भी पक्ष को कम से कम 30 दिन का लिखित नोटिस देना होगा।",
      htmlCond4: "जब तक मालिक से अलग सहमति न हो, यूटिलिटी बिलों की जिम्मेदारी किराएदार की होगी।",
      htmlCond5: "फिक्सचर और फिटिंग्स के रखरखाव की जिम्मेदारी मालिक की है, जब तक नुकसान किराएदार की लापरवाही से न हुआ हो।",
      htmlFooter: "यह सारांश ToLetBook द्वारा रिकॉर्ड में मौजूद टेनेंसी जानकारी से अपने आप बनाया गया है और यह कोई कानूनी रूप से बाध्यकारी दस्तावेज़ नहीं है। औपचारिक रेंटल एग्रीमेंट बनाने के लिए किसी कानूनी विशेषज्ञ से सलाह लें।",
    },
    photoLightbox: {
      closeAria: "बंद करें",
      previousPhotoAria: "पिछली फोटो",
      nextPhotoAria: "अगली फोटो",
      countLabel: "{current} / {total}",
    },
    tenantPlan: {
      title: "प्लान",
      currentPlanLabel: "मौजूदा प्लान",
      basicName: "बेसिक",
      proName: "किराएदार प्रो",
      proSub: "घर ढूंढ रहे किराएदारों के लिए",
      priceValidity: "1 महीने के लिए मान्य",
      featUnlock: "30 दिनों तक अनलिमिटेड मालिक कॉन्टैक्ट नंबर अनलॉक करें",
      featNearby: "हर लिस्टिंग पर पूरा मैप और आस-पास की जगहों तक पहुंच",
      featWhatsapp: "मालिक से सीधे WhatsApp पर बात करें, कोई प्रति-लिस्टिंग फीस नहीं",
      featFallback: "सब्सक्राइब न करने पर भी आपको 1 मालिक का कॉन्टैक्ट मुफ़्त अनलॉक मिलता है — इसके बाद अपग्रेड करना होगा",
      currentPlanBtn: "मौजूदा प्लान",
      subscribeBtn: "₹99 में सब्सक्राइब करें",
      activeUntil: "{date} तक एक्टिव",
      noChargeNote: "अभी कोई भुगतान ज़रूरी नहीं — बिलिंग सेट होने तक सब्सक्राइब करना मुफ्त है।",
      subscribedToast: "किराएदार प्रो एक्टिवेट हो गया",
      switchFailedToast: "आपका प्लान अपडेट नहीं हो सका",
      nearbyUpsellTitle: "मैप और आस-पास की जगहें अनलॉक करें",
      nearbyUpsellBody: "हर लिस्टिंग के पास ट्रांज़िट, ज़रूरी सुविधाएं और स्वास्थ्य सेवा देखने के लिए किराएदार प्रो सब्सक्राइब करें।",
      nearbyUpsellBtn: "किराएदार प्रो देखें",
      cancelPlanBtn: "प्लान कैंसिल करें",
      securePaymentNote: "भुगतान Razorpay के ज़रिए सुरक्षित रूप से प्रोसेस किया जाता है।",
      cancelledToast: "किराएदार प्रो कैंसिल हो गया",
    },
  },
  te: {
    tenantHome: {
      moveOutSettlement: "మూవ్-అవుట్ సెటిల్‌మెంట్",
      yourLastPlace: "మీ చివరి నివాసం",
      depositPaid: "చెల్లించిన డిపాజిట్",
      deductionsLabel: "కోతలు",
      refundableDeposit: "వాపసు చేయదగిన డిపాజిట్",
      exitedOn: "{date} న వెళ్లిపోయారు",
      noActiveTenancyLine1: "మీకు ఇప్పుడు యాక్టివ్ టెనెన్సీ లేదు.",
      noActiveTenancyLine2: "ఇల్లు వెతకడానికి లిస్టింగ్‌లు చూడండి, లేదా షిఫ్ట్ అయ్యాక మీ యజమానిని మిమ్మల్ని జోడించమని అడగండి.",
      welcomeTitle: "ToLetBook కి స్వాగతం!",
      welcomeSub: "మీ కోసం అద్దె ప్రాపర్టీని వెతుకుదాం.",
      browsePropertiesBtn: "ప్రాపర్టీలు చూడండి",
      rentForMonth: "{month} కి అద్దె",
      paidStatus: "చెల్లించారు",
      dueStatus: "బాకీ",
      overdueStatus: "ఓవర్‌డ్యూ",
      dueInDays: "{count} రోజుల్లో గడువు",
      dueTodayLabel: "ఈరోజు గడువు",
      overdueByDays: "{count} రోజులు మించిపోయింది",
      reminderBanner: "🔔 ఈ చెల్లింపు గురించి మీ యజమాని రిమైండర్ పంపారు",
      paidCheck: "✓ చెల్లించారు",
      onDate: "{date} న",
      viaMode: "{mode} ద్వారా",
      receiptBtn: "రసీదు",
      payingBtn: "చెల్లిస్తోంది…",
      payAmount: "{amount} చెల్లించండి",
      paymentRecordedToast: "చెల్లింపు నమోదైంది",
      ownerLabel: "యజమాని",
      callOwnerAria: "యజమానికి కాల్ చేయండి",
      raiseRequest: "రిక్వెస్ట్ పంపండి",
      viewLease: "అగ్రిమెంట్ చూడండి",
      receiptsNav: "రసీదులు",
      browseRooms: "గదులు చూడండి",
      openRequestsHeading: "ఓపెన్ రిక్వెస్ట్‌లు",
      viewAll: "అన్నీ చూడండి",
      noOpenRequests: "ఓపెన్ రిక్వెస్ట్‌లు లేవు.",
      noDetailsProvided: "వివరాలు ఇవ్వలేదు",
    },
    tenantBrowse: {
      occBoys: "బాయ్స్ పీజీ",
      occGirls: "గర్ల్స్ పీజీ",
      occCoEd: "కో-ఎడ్ పీజీ",
      occDefault: "పీజీ",
      filterAll: "అన్నీ",
      filterPg: "పీజీ",
      filter1RK: "1RK",
      filter1BHK: "1BHK",
      filter2BHK: "2BHK",
      filter3BHK: "3BHK",
      filterVilla: "విల్లా",
      filterShop: "షాప్",
      filterOffice: "ఆఫీస్",
      filterShowroom: "షోరూమ్",
      filterWarehouse: "గోడౌన్",
      filterCoworking: "కో-వర్కింగ్",
      filterFactory: "ఫ్యాక్టరీ",
      sortDefault: "డిఫాల్ట్",
      sortPriceAsc: "ధర: తక్కువ నుండి ఎక్కువ",
      sortPriceDesc: "ధర: ఎక్కువ నుండి తక్కువ",
      sortDistance: "దూరం: దగ్గరగా ఉన్నవి ముందు",
      locationNotSupported: "ఈ బ్రౌజర్‌లో లొకేషన్ సపోర్ట్ లేదు",
      locationPermissionDenied: "లొకేషన్ అనుమతి తిరస్కరించబడింది",
      locationCouldNotGet: "మీ లొకేషన్ తెలుసుకోలేకపోయాము",
      heading: "మీ కోసం అద్దె ఇళ్లను వెతుకుదాం",
      searchPlaceholder: "ప్రాంతం లేదా ప్రాపర్టీ పేరుతో వెతకండి",
      filtersBtn: "⚙ ఫిల్టర్లు",
      listView: "☰ లిస్ట్ వ్యూ",
      mapView: "🗺️ మ్యాప్ వ్యూ",
      fromPrice: "{price} నుండి మొదలు",
      housesCount: "{count} ఇళ్లు",
      noVacancies: "ఇప్పుడు ఖాళీ లేవు — తర్వాత చూడండి.",
      noMatch: "మీ సెర్చ్ లేదా ఫిల్టర్‌కు సరిపోయే లిస్టింగ్‌లు లేవు.",
      noPinnedLocation: "వీటిలో ఏ లిస్టింగ్‌కు లొకేషన్ పిన్ చేయలేదు.",
      distanceAway: "{value} దూరంలో",
      filtersTitle: "ఫిల్టర్లు",
      propertyTypeLabel: "ప్రాపర్టీ రకం",
      sortByLabel: "దీని ప్రకారం క్రమం చేయండి",
      yourLocationLabel: "మీ లొకేషన్",
      locating: "లొకేషన్ కనుగొంటోంది…",
      locationOn: "📍 లొకేషన్ ఆన్‌లో ఉంది",
      useMyLocation: "📍 నా లొకేషన్ వాడండి",
      nearLabel: "📍 {location} సమీపంలో",
      nearYou: "మీ దగ్గర",
      findingLocation: "మీ లొకేషన్ కనుక్కుంటున్నాం…",
      allowLocationPrompt: "దగ్గరలోని ఇళ్లు చూడడానికి లొకేషన్ అనుమతి ఇవ్వండి",
      updateLocation: "అప్డేట్",
      withinKm: "{km} కి.మీ లోపల",
      allAreas: "అన్ని ప్రాంతాలు",
      noneNearby: "మీ నుండి {km} కి.మీ లోపల ఇంకా ప్రాపర్టీలు లేవు.",
      showAllProperties: "అన్ని ప్రాపర్టీలు చూపించండి",
      clearAll: "అన్నీ క్లియర్ చేయండి",
      showResults: "ఫలితాలు చూపించు",
      backToListings: "‹ లిస్టింగ్‌లకు తిరిగి వెళ్ళండి",
      housesHeading: "ఇళ్లు",
      occupiedBadge: "నిండిపోయింది",
      vacantBadge: "ఖాళీ",
      typeLabel: "రకం",
      rentLabel: "అద్దె",
      perMonthSuffix: "/నెల",
      depositLabel: "డిపాజిట్",
      escalationLabel: "అద్దె పెరుగుదల",
      yearsSuffix: "సంవత్సరాలు",
      occupancyLabel: "ఆక్యుపెన్సీ",
      mealPlanLabel: "భోజనం చేర్చబడింది",
      preferredTenantsLabel: "ప్రాధాన్య అద్దెదారులు",
      roomAc: "ఏసీ",
      roomAttachedBathroom: "అటాచ్డ్ బాత్రూమ్",
      bedsVacant: "{total} లో {vacant} బెడ్‌లు ఖాళీగా ఉన్నాయి",
      locationLabel: "లొకేషన్",
      openInGoogleMaps: "Google Maps లో తెరవండి",
      viewOnMap: "మ్యాప్‌లో చూడండి",
      hideMap: "మ్యాప్ దాచండి",
      showNearby: "సమీపంలో ఏముందో చూడండి",
      hideNearby: "సమీప ప్రదేశాలను దాచండి",
      loadingNearby: "సమీప ప్రదేశాలను వెతుకుతోంది…",
      nearbyEmpty: "సమీపంలో ప్రదేశాలు కనుగొనబడలేదు",
      nearbyTransit: "ట్రాన్సిట్",
      nearbyEssentials: "నిత్యావసరాలు",
      nearbyHealthcare: "ఆరోగ్య సంరక్షణ",
      metersAway: "{distance} మీ దూరంలో",
      similarPropertiesHeading: "సారూప్య ప్రాపర్టీలు",
      topTag: "టాప్",
      featuredTag: "ఫీచర్డ్",
      moveInHint: "షిఫ్ట్ అవ్వడానికి, మీ రిజిస్టర్డ్ ఫోన్ నంబర్‌తో మిమ్మల్ని అద్దెదారుగా జోడించమని యజమానిని అడగండి — తర్వాత అది మీ హోమ్ ట్యాబ్‌లో అదే అవుతుంది.",
      scheduleVisit: "విజిట్ షెడ్యూల్ చేయండి",
      pickDateTime: "తేదీ మరియు సమయం ఎంచుకోండి",
      visitRequestSent: "విజిట్ రిక్వెస్ట్ యజమానికి పంపబడింది",
      couldNotSendVisit: "విజిట్ రిక్వెస్ట్ పంపలేకపోయాము",
      requestedFor: "{date} న {time} కి రిక్వెస్ట్ పంపబడింది — యజమానికి తెలియజేయబడింది, వారు నిర్ధారిస్తారు.",
      dateLabel: "తేదీ",
      timeLabel: "సమయం",
      noteOptionalLabel: "నోట్ (ఐచ్ఛికం)",
      notePlaceholder: "ఉదా. సాయంత్రం వస్తే బాగుంటుంది, ముందుగా కాల్ చేస్తాను",
      sending: "పంపుతోంది…",
      requestVisitBtn: "విజిట్ రిక్వెస్ట్ చేయండి",
      ownerContactLabel: "యజమాని",
      verifiedOwnerBadge: "ధృవీకృత",
      unlockingBtn: "అన్‌లాక్ అవుతోంది…",
      unlockNumberFreeBtn: "🔓 నంబర్ అన్‌లాక్ చేయండి — ఉచితం",
      unlockUpsellTitle: "ఉచిత అన్‌లాక్ వాడేశారు",
      unlockUpsellBody: "బేసిక్ ప్లాన్‌లో 1 యజమాని కాంటాక్ట్ ఉచితంగా అన్‌లాక్ అవుతుంది. అపరిమిత యజమానులను అన్‌లాక్ చేయడానికి Tenant Pro‌కి అప్‌గ్రేడ్ చేయండి.",
      unlockUpsellBtn: "Tenant Pro‌కి అప్‌గ్రేడ్ చేయండి",
      callNumberBtn: "{phone} కి కాల్ చేయండి",
      whatsappBtn: "💬 WhatsApp‌లో చాట్ చేయండి",
      whatsappMessage: "నమస్తే, ToLetBookలో లిస్ట్ చేసిన మీ ప్రాపర్టీ \"{property}\" పట్ల నాకు ఆసక్తి ఉంది. ఇది ఇంకా అందుబాటులో ఉందా?",
      contactUnlockedToast: "కాంటాక్ట్ నంబర్ అన్‌లాక్ అయ్యింది",
      couldNotUnlockToast: "కాంటాక్ట్ అన్‌లాక్ చేయలేకపోయాము",
    },
    tenantPayments: {
      noActiveTenancy: "మీకు అద్దె చెల్లించడానికి యాక్టివ్ టెనెన్సీ లేదు.",
      title: "పేమెంట్స్",
      paidStatus: "చెల్లించారు",
      rentDue: "అద్దె బాకీ ఉంది",
      reminderBanner: "🔔 ఈ చెల్లింపు గురించి మీ యజమాని రిమైండర్ పంపారు",
      payingBtn: "చెల్లిస్తోంది…",
      payViaUpi: "UPI ద్వారా {amount} చెల్లించండి",
      payNow: "{amount} చెల్లించండి",
      paymentFailedToast: "చెల్లింపు జరగలేదు — మళ్లీ ప్రయత్నించండి",
      paymentRecordedToast: "చెల్లింపు నమోదైంది",
      viewReceiptBtn: "రసీదు చూడండి",
      history: "హిస్టరీ",
      payViaUpiDirectlyBtn: "నేరుగా UPI ద్వారా చెల్లించండి",
      ownerUpiIdLabel: "ఓనర్ UPI ID",
      copyUpiBtn: "కాపీ చేయండి",
      upiIdCopiedToast: "UPI ID కాపీ అయింది",
      openUpiAppBtn: "UPI యాప్ తెరవండి",
      markPaidUpiBtn: "నేను UPI ద్వారా చెల్లించాను",
      markPaidUpiConfirm: "మీరు ఈ అద్దెను ఓనర్ UPI ID కి పంపారా? దీనితో ఇది చెల్లించినట్లు గుర్తించబడుతుంది.",
    },
    tenantRequests: {
      title: "మెయింటెనెన్స్ రిక్వెస్ట్‌లు",
      cancelLink: "రద్దు చేయండి ›",
      newLink: "+ కొత్తది ›",
      whatsWrong: "సమస్య ఏమిటి?",
      issuePlaceholder: "ఉదా. కుళాయి నుండి నీరు కారడం",
      detailsOptional: "వివరాలు (ఐచ్ఛికం)",
      submittingBtn: "సబ్మిట్ అవుతోంది…",
      submitRequestBtn: "రిక్వెస్ట్ సబ్మిట్ చేయండి",
      giveTitleError: "రిక్వెస్ట్‌కు ఒక టైటిల్ ఇవ్వండి",
      requestRaisedToast: "రిక్వెస్ట్ పంపబడింది",
      noRequestsYet: "ఇంకా ఏ రిక్వెస్ట్ పెట్టలేదు.",
      noDetailsProvided: "వివరాలు ఇవ్వలేదు",
      myVisitsHeading: "నా విజిట్ రిక్వెస్ట్‌లు",
      emptyNoVisits: "మీరు ఇంకా ఏ ప్రాపర్టీ విజిట్ రిక్వెస్ట్ చేయలేదు.",
      visitLine: "{date} న {time}కి",
      cancelVisitButton: "రద్దు చేయండి",
      visitCancelledToast: "విజిట్ రిక్వెస్ట్ రద్దు చేయబడింది",
      visitStatusPending: "పెండింగ్‌లో",
      visitStatusConfirmed: "కన్ఫర్మ్ అయింది",
      visitStatusDeclined: "తిరస్కరించారు",
      visitStatusCancelled: "రద్దు చేయబడింది",
      vacateNoticeHeading: "వేకేట్ నోటీసు",
      giveNoticeLink: "+ నోటీసు ఇవ్వండి ›",
      vacateOnLabel: "{date}న ఖాళీ చేస్తారు",
      noticeGivenOnLabel: "{date}న నోటీసు ఇచ్చారు",
      vacateStatusPending: "పెండింగ్",
      vacateStatusAcknowledged: "ఆమోదించబడింది",
      vacateStatusCancelled: "రద్దు చేయబడింది",
      shareWhatsappBtn: "💬 WhatsApp‌లో షేర్ చేయండి",
      cancelNoticeBtn: "నోటీసు రద్దు చేయండి",
      noticeDateLabel: "నోటీసు తేదీ",
      vacateDatePreview: "మీ {days}-రోజుల నోటీసు వ్యవధి ప్రకారం, మీరు {date}న ఖాళీ చేస్తారు",
      submitVacateNoticeBtn: "వేకేట్ నోటీసు సమర్పించండి",
      vacateNoticeSentToast: "వేకేట్ నోటీసు యజమానికి పంపబడింది",
      vacateNoticeCancelledToast: "వేకేట్ నోటీసు రద్దు చేయబడింది",
      emptyNoVacateNotices: "మీరు ఇంకా ఏ వేకేట్ నోటీసు ఇవ్వలేదు.",
      vacateWhatsappMessage: "నమస్తే {owner}, నేను \"{property}\" ఖాళీ చేస్తున్నానని అధికారికంగా తెలియజేయడానికి ఇది. నోటీసు {noticeDate}న ఇచ్చాను, {vacateDate}న ఖాళీ చేస్తాను.",
    },
    tenantOnboarding: {
      title: "మీ మూవ్-ఇన్ పూర్తి చేయండి",
      nameLabel: "మీ పేరు (ఆధార్ ప్రకారం)",
      nameHint: "ఈ ఖచ్చితమైన పేరు మీ రెంటల్ అగ్రిమెంట్‌పై ఉపయోగించబడుతుంది.",
      rentLabel: "నెలవారీ అద్దె",
      dueDayLabel: "అద్దె చెల్లించాల్సిన రోజు",
      dueDayValue: "ప్రతి నెల {day}వ తేదీ",
      depositLabel: "సెక్యూరిటీ డిపాజిట్",
      depositPaidBadge: "చెల్లించారు",
      depositPendingBadge: "బాకీ",
      depositConfirmingBadge: "నిర్ధారిస్తోంది…",
      payNowBtn: "ఇప్పుడు చెల్లించండి",
      payingBtn: "పేమెంట్ తెరుచుకుంటోంది…",
      depositPaymentDesc: "సెక్యూరిటీ డిపాజిట్",
      depositPaidToast: "డిపాజిట్ చెల్లింపు అందింది",
      depositFailedToast: "చెల్లింపు జరగలేదు — మళ్లీ ప్రయత్నించండి",
      depositPaidCashBadge: "నగదులో చెల్లించారు",
      paidInCashLink: "నేను నగదులో చెల్లించాను",
      cashProofHint: "రసీదు లేదా చెల్లింపు రుజువు ఫోటో జోడించండి (ఐచ్ఛికం).",
      cashProofUploadLabel: "రుజువు జోడించండి",
      confirmCashBtn: "నగదు చెల్లింపును నిర్ధారించండి",
      errDepositRequired: "కొనసాగించే ముందు సెక్యూరిటీ డిపాజిట్ చెల్లించండి (ఆన్‌లైన్ లేదా నగదు)",
      amenitiesLabel: "యజమాని అందించిన సౌకర్యాలు",
      identityVerificationLabel: "గుర్తింపు ధృవీకరణ",
      yourIdentityLabel: "మీరు",
      identityVerificationHint: "మీ ప్రభుత్వ-ధృవీకరించిన పేరు రెంటల్ అగ్రిమెంట్‌పై కనిపించేలా DigiLocker ద్వారా మీ గుర్తింపును ధృవీకరించండి.",
      confirmLabel: "నేను పైన ఉన్న నా మూవ్-ఇన్ వివరాలను సమీక్షించాను మరియు అవి సరైనవని కన్ఫర్మ్ చేస్తున్నాను.",
      submitBtn: "సెటప్ పూర్తి చేయండి",
      errNameRequired: "మీ పేరు నమోదు చేయండి",
      errMustConfirm: "దయచేసి మీరు వివరాలను సమీక్షించారని కన్ఫర్మ్ చేయండి",
      completedToast: "మూవ్-ఇన్ వివరాలు కన్ఫర్మ్ అయ్యాయి",
    },
    moveInDetails: {
      title: "మూవ్-ఇన్ వివరాలు",
    },
    locationCapture: {
      pinLocationLabel: "ఈ ప్రాపర్టీ లొకేషన్‌ను పిన్ చేయండి",
      panMapHint: "📌 సరైన స్థానంలో పిన్ ఉంచడానికి మ్యాప్‌ను తరలించండి",
      expandMapAria: "మ్యాప్‌ను విస్తరించండి",
      clear: "క్లియర్",
      searchPlaceholder: "చిరునామా కోసం వెతకండి",
      loadingSearch: "సెర్చ్ లోడ్ అవుతోంది…",
      or: "లేదా",
      locating: "లొకేషన్ కనుగొంటోంది…",
      useCurrentLocation: "📍 ప్రస్తుత లొకేషన్ వాడండి",
      pickSuggestion: "డ్రాప్‌డౌన్ నుండి ఒక సూచనను ఎంచుకోండి",
      locationNotSupported: "ఈ బ్రౌజర్‌లో లొకేషన్ సపోర్ట్ లేదు",
      locationPermissionDenied: "లొకేషన్ అనుమతి తిరస్కరించబడింది",
      locationCouldNotGet: "మీ లొకేషన్ తెలుసుకోలేకపోయాము",
      hint: "చిరునామా వెతకండి లేదా కింద ప్రాంతం ఆటోమేటిక్‌గా నింపడానికి GPS తాకండి — డోర్ నంబర్ మరియు బిల్డింగ్ పేరు మీరే నమోదు చేయాలి.",
    },
    googleMap: {
      yourLocation: "మీ లొకేషన్",
      couldNotLoad: "Google Maps లోడ్ కాలేదు — API కీని తనిఖీ చేయండి",
      viewDetails: "వివరాలు చూడండి ›",
    },
    rentReceipt: {
      back: "‹ వెనక్కి",
      paymentRecordedBanner: "✓ చెల్లింపు నమోదైంది — ఇదిగో మీ రసీదు.",
      title: "అద్దె రసీదు",
      amountPaid: "చెల్లించిన మొత్తం",
      receiptNoLabel: "రసీదు నంబర్",
      paidByLabel: "చెల్లించినవారు",
      propertyLabel: "ప్రాపర్టీ",
      receivedByLabel: "అందుకున్నవారు",
      forMonthLabel: "ఏ నెలకు",
      paymentModeLabel: "చెల్లింపు విధానం",
      paidOnLabel: "చెల్లించిన తేదీ",
      downloadBtn: "డౌన్‌లోడ్ చేయండి",
      shareBtn: "షేర్ చేయండి",
      htmlTitle: "అద్దె రసీదు - {month}",
      htmlStamp: "✓ చెల్లించారు",
      htmlAmountPaidLabel: "చెల్లించిన మొత్తం",
      htmlFooter: "ఇది ToLetBook నుండి సిస్టమ్-జనరేటెడ్ రసీదు. ఇక్కడ చూపిన అద్దె యాప్‌లో నమోదు చేయబడింది, నిజమైన పేమెంట్ గేట్‌వే ద్వారా ప్రాసెస్ చేయబడలేదు.",
      shareTitle: "అద్దె రసీదు - {month}",
      shareText: "రసీదు {receiptNo}, మొత్తం {amount}",
    },
    rentalAgreement: {
      back: "‹ వెనక్కి",
      title: "రెంటల్ అగ్రిమెంట్",
      ownerLabel: "యజమాని",
      tenantLabel: "అద్దెదారు",
      propertyLabel: "ప్రాపర్టీ",
      typeLabel: "రకం",
      monthlyRentLabel: "నెలవారీ అద్దె",
      securityDepositLabel: "సెక్యూరిటీ డిపాజిట్",
      moveInDateLabel: "షిఫ్ట్ అయిన తేదీ",
      disclaimer: "ఇది రికార్డులో ఉన్న టెనెన్సీ వివరాల నుండి ఆటోమేటిక్‌గా తయారైన సారాంశం, ఇది చట్టపరంగా కట్టుబడి ఉండే డాక్యుమెంట్ కాదు. సాధారణ డెమో నిబంధనలు: ప్రతి నెలా 5వ తేదీలోపు అద్దె చెల్లించాలి, టెనెన్సీ ముగించడానికి 30 రోజుల నోటీసు, సాధారణ అరుగుదల కంటే ఎక్కువ నష్టాలకు కోత పోగా డిపాజిట్ వాపసు అవుతుంది.",
      downloadAgreementBtn: "అగ్రిమెంట్ డౌన్‌లోడ్ చేయండి",
      stampStatusLabel: "E-Stamp స్టేటస్",
      stampStatusNotStarted: "ప్రారంభం కాలేదు",
      stampStatusProcessing: "ప్రాసెస్ అవుతోంది",
      stampStatusVerified: "వెరిఫై అయింది",
      stampHint: "ఈ అగ్రిమెంట్‌ను ప్రభుత్వం నుండి చట్టబద్ధంగా E-Stamp చేయించండి. దీనికి {amount} నిజమైన ప్రభుత్వ ఫీజు వర్తిస్తుంది.",
      stampFeeAlreadyPaidHint: "మీ E-Stamp ఫీజు చెల్లించబడింది — ఆర్డర్ మాత్రం ఇంకా పెట్టలేకపోయాము. ఎలాంటి అదనపు ఛార్జీ లేకుండా కింద మళ్లీ ప్రయత్నించండి.",
      sendForStampBtn: "{amount} చెల్లించి E-Stamp కోసం పంపండి",
      retrySendForStampBtn: "E-Stamp కోసం మళ్లీ పంపండి",
      sendingStampBtn: "పంపుతోంది…",
      confirmingPaymentBtn: "చెల్లింపు నిర్ధారిస్తోంది…",
      stampFeePaymentDesc: "ప్రభుత్వ E-Stamp ఫీజు",
      stampFeeFailedToast: "చెల్లింపు విఫలమైంది — మళ్లీ ప్రయత్నించండి",
      stampSentToast: "ప్రభుత్వ E-Stamping కోసం పంపబడింది",
      refreshStatusBtn: "స్టేటస్ రిఫ్రెష్ చేయండి",
      viewStampedBtn: "స్టాంప్ చేసిన అగ్రిమెంట్ చూడండి",
      identityVerificationLabel: "గుర్తింపు వెరిఫికేషన్",
      digilockerVerifiedBadge: "వెరిఫై అయింది",
      digilockerNotVerifiedBadge: "వెరిఫై కాలేదు",
      verifyDigilockerBtn: "DigiLocker తో వెరిఫై చేయండి",
      verifyingDigilockerBtn: "DigiLocker తెరుచుకుంటోంది…",
      digilockerVerifiedToast: "{name} గా వెరిఫై అయింది",
      digilockerDeniedToast: "DigiLocker వెరిఫికేషన్ రద్దు చేయబడింది",
      digilockerExpiredToast: "ఆ DigiLocker లింక్ గడువు ముగిసింది — మళ్లీ ప్రయత్నించండి",
      comingSoonTitle: "ప్రభుత్వ ఈ-స్టాంపింగ్ త్వరలో వస్తోంది",
      comingSoonBody: "మీరు యాప్ నుండే పూర్తిగా ప్రభుత్వ-ధృవీకరించిన, ఈ-స్టాంప్డ్ రెంటల్ అగ్రిమెంట్ రూపొందించుకునేలా మేము స్టాంప్ డ్యూటీ చెల్లింపులను సిద్ధం చేస్తున్నాము.",
      interestBtn: "నాకు ఆసక్తి ఉంది — తెలియజేయండి",
      interestRegisteredBtn: "మీరు లిస్ట్‌లో ఉన్నారు ✓",
      interestToast: "ధన్యవాదాలు! ఈ-స్టాంపింగ్ లైవ్ అయినప్పుడు మేము మీకు తెలియజేస్తాము.",
      uploadDocHeading: "ఇప్పటికే రెంటల్ అగ్రిమెంట్ ఉందా?",
      uploadDocHint: "మీరు ToLetBook బయట ఇప్పటికే ఒకటి తయారు చేసుకున్నట్లయితే, ఈ టెనెన్సీతో సేవ్ అయ్యేలా ఇక్కడ అప్‌లోడ్ చేయండి.",
      uploadDocBtn: "రెంటల్ అగ్రిమెంట్ అప్‌లోడ్ చేయండి",
      viewUploadedDocBtn: "అప్‌లోడ్ చేసిన అగ్రిమెంట్ చూడండి",
      removeUploadedDocBtn: "తీసివేయండి",
      uploadedOnLabel: "{date} న అప్‌లోడ్ చేయబడింది",
      noUploadedDocTenantHint: "మీ యజమాని ఇంకా రెంటల్ అగ్రిమెంట్ అప్‌లోడ్ చేయలేదు.",
      uploadDocSuccessToast: "రెంటల్ అగ్రిమెంట్ అప్‌లోడ్ అయ్యింది",
      uploadDocFailedToast: "అప్‌లోడ్ చేయలేకపోయాము — 8MB కంటే తక్కువ PDF లేదా ఇమేజ్ ప్రయత్నించండి",
      removeDocConfirm: "అప్‌లోడ్ చేసిన రెంటల్ అగ్రిమెంట్‌ను తీసివేయాలా?",
      removeDocSuccessToast: "రెంటల్ అగ్రిమెంట్ తీసివేయబడింది",
      htmlTitle: "రెంటల్ అగ్రిమెంట్ - {property}",
      htmlDocTitle: "రెంటల్ అగ్రిమెంట్ సారాంశం",
      htmlAgreementNo: "అగ్రిమెంట్ నంబర్ {no}",
      htmlOwnerLessor: "యజమాని / లెసర్",
      htmlTenantLessee: "అద్దెదారు / లెసీ",
      htmlPropertyHeading: "ప్రాపర్టీ",
      htmlAddressLabel: "చిరునామా",
      htmlTypeLabel: "రకం",
      htmlTermsHeading: "నిబంధనలు",
      htmlMonthlyRentLabel: "నెలవారీ అద్దె",
      htmlSecurityDepositLabel: "సెక్యూరిటీ డిపాజిట్",
      htmlMoveInDateLabel: "షిఫ్ట్ అయిన తేదీ",
      htmlConditionsHeading: "షరతులు",
      htmlCond1: "అద్దె ప్రతి నెలా 5వ తేదీలోపు, అంగీకరించిన విధానంలో యజమానికి చెల్లించాలి.",
      htmlCond2: "సాధారణ అరుగుదల కంటే ఎక్కువ నష్టాలకు కోత పోగా, షిఫ్ట్ అయ్యేటప్పుడు సెక్యూరిటీ డిపాజిట్ వాపసు చేయబడుతుంది.",
      htmlCond3: "టెనెన్సీ ముగించే ముందు ఇరు పక్షాలు కనీసం 30 రోజుల ముందు లిఖితపూర్వక నోటీసు ఇవ్వాలి.",
      htmlCond4: "యజమానితో వేరే విధంగా అంగీకరించకపోతే, యుటిలిటీ బిల్లుల బాధ్యత అద్దెదారుదే.",
      htmlCond5: "అద్దెదారు నిర్లక్ష్యం వల్ల నష్టం జరగకపోతే, ఫిక్చర్లు మరియు ఫిట్టింగుల మెయింటెనెన్స్ బాధ్యత యజమానిదే.",
      htmlFooter: "ఈ సారాంశం రికార్డులో ఉన్న టెనెన్సీ వివరాల నుండి ToLetBook ద్వారా ఆటోమేటిక్‌గా తయారు చేయబడింది మరియు ఇది చట్టపరంగా కట్టుబడి ఉండే డాక్యుమెంట్ కాదు. అధికారిక రెంటల్ అగ్రిమెంట్ తయారు చేయడానికి న్యాయ నిపుణుడిని సంప్రదించండి.",
    },
    photoLightbox: {
      closeAria: "మూసివేయండి",
      previousPhotoAria: "మునుపటి ఫోటో",
      nextPhotoAria: "తర్వాతి ఫోటో",
      countLabel: "{current} / {total}",
    },
    tenantPlan: {
      title: "ప్లాన్",
      currentPlanLabel: "ప్రస్తుత ప్లాన్",
      basicName: "బేసిక్",
      proName: "అద్దెదారు ప్రో",
      proSub: "ఇల్లు వెతుకుతున్న అద్దెదారుల కోసం",
      priceValidity: "1 నెల వరకు చెల్లుతుంది",
      featUnlock: "30 రోజుల పాటు అపరిమిత యజమాని కాంటాక్ట్ నంబర్‌లను అన్‌లాక్ చేయండి",
      featNearby: "ప్రతి లిస్టింగ్‌లో పూర్తి మ్యాప్ మరియు సమీప-ప్రదేశాల యాక్సెస్",
      featWhatsapp: "యజమానితో నేరుగా WhatsApp‌లో మాట్లాడండి, ప్రతి-లిస్టింగ్ ఫీజు లేదు",
      featFallback: "సబ్‌స్క్రైబ్ చేయకపోయినా 1 యజమాని కాంటాక్ట్ ఉచితంగా అన్‌లాక్ అవుతుంది — తర్వాత అప్‌గ్రేడ్ చేయాలి",
      currentPlanBtn: "ప్రస్తుత ప్లాన్",
      subscribeBtn: "₹99కి సబ్‌స్క్రైబ్ చేయండి",
      activeUntil: "{date} వరకు యాక్టివ్",
      noChargeNote: "ఇంకా చెల్లింపు అవసరం లేదు — బిల్లింగ్ సెటప్ అయ్యే వరకు సబ్‌స్క్రైబ్ చేయడం ఉచితం.",
      subscribedToast: "అద్దెదారు ప్రో యాక్టివేట్ అయ్యింది",
      switchFailedToast: "మీ ప్లాన్ అప్‌డేట్ చేయలేకపోయాము",
      nearbyUpsellTitle: "మ్యాప్ మరియు సమీప ప్రదేశాలను అన్‌లాక్ చేయండి",
      nearbyUpsellBody: "ప్రతి లిస్టింగ్ దగ్గర ట్రాన్సిట్, నిత్యావసరాలు మరియు ఆరోగ్య సంరక్షణ చూడటానికి అద్దెదారు ప్రోకి సబ్‌స్క్రైబ్ చేయండి.",
      nearbyUpsellBtn: "అద్దెదారు ప్రో చూడండి",
      cancelPlanBtn: "ప్లాన్ రద్దు చేయండి",
      securePaymentNote: "చెల్లింపులు Razorpay ద్వారా సురక్షితంగా ప్రాసెస్ చేయబడతాయి.",
      cancelledToast: "టెనెంట్ ప్రో రద్దు చేయబడింది",
    },
  },
  ta: {
    tenantHome: {
      moveOutSettlement: "வெளியேறும் தீர்வு",
      yourLastPlace: "உங்கள் கடைசி இடம்",
      depositPaid: "செலுத்திய டெபாசிட்",
      deductionsLabel: "கழிவுகள்",
      refundableDeposit: "திரும்பப் பெறக்கூடிய டெபாசிட்",
      exitedOn: "{date} அன்று வெளியேறியது",
      noActiveTenancyLine1: "உங்களுக்கு தற்போது செயலில் உள்ள குடியிருப்பு இல்லை.",
      noActiveTenancyLine2: "இடம் தேட பட்டியல்களைப் பாருங்கள், அல்லது குடிபெயர்ந்த பிறகு உங்கள் உரிமையாளரிடம் உங்களைச் சேர்க்கச் சொல்லுங்கள்.",
      welcomeTitle: "ToLetBook-க்கு வரவேற்கிறோம்!",
      welcomeSub: "உங்களுக்கான வாடகை சொத்தைக் கண்டுபிடிப்போம்.",
      browsePropertiesBtn: "சொத்துகளைப் பார்",
      rentForMonth: "{month} மாத வாடகை",
      paidStatus: "செலுத்தப்பட்டது",
      dueStatus: "நிலுவை",
      overdueStatus: "தாமதம்",
      dueInDays: "{count} நாட்களில் செலுத்த வேண்டும்",
      dueTodayLabel: "இன்று செலுத்த வேண்டும்",
      overdueByDays: "{count} நாட்கள் தாமதம்",
      reminderBanner: "🔔 இந்த பணம் செலுத்துதல் குறித்து உங்கள் உரிமையாளர் நினைவூட்டல் அனுப்பியுள்ளார்",
      paidCheck: "✓ செலுத்தப்பட்டது",
      onDate: "{date} அன்று",
      viaMode: "{mode} மூலம்",
      receiptBtn: "ரசீது",
      payingBtn: "செலுத்துகிறது…",
      payAmount: "{amount} செலுத்து",
      paymentRecordedToast: "பணம் செலுத்துதல் பதிவு செய்யப்பட்டது",
      ownerLabel: "உரிமையாளர்",
      callOwnerAria: "உரிமையாளரை அழை",
      raiseRequest: "கோரிக்கை அனுப்பு",
      viewLease: "ஒப்பந்தத்தைப் பார்",
      receiptsNav: "ரசீதுகள்",
      browseRooms: "அறைகளைப் பார்",
      openRequestsHeading: "திறந்த கோரிக்கைகள்",
      viewAll: "அனைத்தையும் பார்",
      noOpenRequests: "திறந்த கோரிக்கைகள் இல்லை.",
      noDetailsProvided: "விவரங்கள் இல்லை",
    },
    tenantBrowse: {
      occBoys: "பாய்ஸ் பிஜி",
      occGirls: "கேர்ல்ஸ் பிஜி",
      occCoEd: "கோ-எட் பிஜி",
      occDefault: "பிஜி",
      filterAll: "அனைத்தும்",
      filterPg: "பிஜி",
      filter1RK: "1RK",
      filter1BHK: "1BHK",
      filter2BHK: "2BHK",
      filter3BHK: "3BHK",
      filterVilla: "வில்லா",
      filterShop: "கடை",
      filterOffice: "அலுவலகம்",
      filterShowroom: "ஷோரூம்",
      filterWarehouse: "கிடங்கு",
      filterCoworking: "கோ-வொர்க்கிங்",
      filterFactory: "தொழிற்சாலை",
      sortDefault: "இயல்பு",
      sortPriceAsc: "விலை: குறைவிலிருந்து அதிகம்",
      sortPriceDesc: "விலை: அதிகத்திலிருந்து குறைவு",
      sortDistance: "தூரம்: அருகில் உள்ளவை",
      locationNotSupported: "இந்த பிரவுசரில் இருப்பிடம் ஆதரிக்கப்படவில்லை",
      locationPermissionDenied: "இருப்பிட அனுமதி மறுக்கப்பட்டது",
      locationCouldNotGet: "உங்கள் இருப்பிடத்தைப் பெற முடியவில்லை",
      heading: "உங்களுக்கான வாடகை இடங்களைத் தேடுவோம்",
      searchPlaceholder: "பகுதி அல்லது சொத்து பெயரில் தேடுங்கள்",
      filtersBtn: "⚙ வடிகட்டிகள்",
      listView: "☰ பட்டியல் காட்சி",
      mapView: "🗺️ வரைபட காட்சி",
      fromPrice: "{price} முதல்",
      housesCount: "{count} வீடுகள்",
      noVacancies: "இப்போது காலி இடங்கள் இல்லை — பிறகு பாருங்கள்.",
      noMatch: "உங்கள் தேடல் அல்லது வடிகட்டிக்கு பொருந்தும் பட்டியல்கள் இல்லை.",
      noPinnedLocation: "இவற்றில் எந்த பட்டியலுக்கும் இருப்பிடம் பின் செய்யப்படவில்லை.",
      distanceAway: "{value} தொலைவில்",
      filtersTitle: "வடிகட்டிகள்",
      propertyTypeLabel: "சொத்து வகை",
      sortByLabel: "இதன்படி வரிசைப்படுத்து",
      yourLocationLabel: "உங்கள் இருப்பிடம்",
      locating: "இருப்பிடம் கண்டறிகிறது…",
      locationOn: "📍 இருப்பிடம் ஆன்",
      useMyLocation: "📍 எனது இருப்பிடத்தைப் பயன்படுத்து",
      nearLabel: "📍 {location} அருகில்",
      nearYou: "உங்கள் அருகில்",
      findingLocation: "உங்கள் இருப்பிடத்தைத் தேடுகிறோம்…",
      allowLocationPrompt: "அருகிலுள்ள வீடுகளைப் பார்க்க இருப்பிட அனுமதி வழங்கவும்",
      updateLocation: "புதுப்பி",
      withinKm: "{km} கி.மீ.க்குள்",
      allAreas: "அனைத்து பகுதிகள்",
      noneNearby: "உங்களிடமிருந்து {km} கி.மீ.க்குள் இன்னும் சொத்துகள் இல்லை.",
      showAllProperties: "அனைத்து சொத்துகளையும் காட்டு",
      clearAll: "அனைத்தையும் அழி",
      showResults: "முடிவுகளைக் காட்டு",
      backToListings: "‹ பட்டியல்களுக்குத் திரும்பு",
      housesHeading: "வீடுகள்",
      occupiedBadge: "நிரம்பியது",
      vacantBadge: "காலி",
      typeLabel: "வகை",
      rentLabel: "வாடகை",
      perMonthSuffix: "/மாதம்",
      depositLabel: "டெபாசிட்",
      escalationLabel: "வாடகை உயர்வு",
      yearsSuffix: "ஆண்டுகள்",
      occupancyLabel: "ஆக்குபன்சி",
      mealPlanLabel: "உணவு வசதி",
      preferredTenantsLabel: "விருப்பமான குடியிருப்பாளர்கள்",
      roomAc: "ஏசி",
      roomAttachedBathroom: "இணைந்த குளியலறை",
      bedsVacant: "{total} இல் {vacant} படுக்கைகள் காலி",
      locationLabel: "இருப்பிடம்",
      openInGoogleMaps: "Google Maps இல் திற",
      viewOnMap: "மேப்பில் காண்க",
      hideMap: "மேப்பை மறை",
      showNearby: "அருகில் என்ன உள்ளது எனப் பார்க்க",
      hideNearby: "அருகிலுள்ள இடங்களை மறை",
      loadingNearby: "அருகிலுள்ள இடங்களைத் தேடுகிறது…",
      nearbyEmpty: "அருகில் இடங்கள் எதுவும் இல்லை",
      nearbyTransit: "போக்குவரத்து",
      nearbyEssentials: "அத்தியாவசியங்கள்",
      nearbyHealthcare: "சுகாதாரம்",
      metersAway: "{distance} மீ தொலைவில்",
      similarPropertiesHeading: "ஒத்த சொத்துகள்",
      topTag: "டாப்",
      featuredTag: "சிறப்பு",
      moveInHint: "குடிபெயர, உங்கள் பதிவு செய்யப்பட்ட மொபைல் எண்ணுடன் உங்களை குடியிருப்பாளராக சேர்க்கும்படி உரிமையாளரிடம் கேளுங்கள் — பிறகு அது தானாக உங்கள் முகப்பு தாவலில் தெரியும்.",
      scheduleVisit: "வருகையை திட்டமிடு",
      pickDateTime: "தேதி மற்றும் நேரத்தைத் தேர்ந்தெடுக்கவும்",
      visitRequestSent: "வருகை கோரிக்கை உரிமையாளருக்கு அனுப்பப்பட்டது",
      couldNotSendVisit: "வருகை கோரிக்கையை அனுப்ப முடியவில்லை",
      requestedFor: "{date} அன்று {time} மணிக்கு கோரிக்கை அனுப்பப்பட்டது — உரிமையாளருக்குத் தெரிவிக்கப்பட்டது, அவர் உறுதிசெய்வார்.",
      dateLabel: "தேதி",
      timeLabel: "நேரம்",
      noteOptionalLabel: "குறிப்பு (விருப்பம்)",
      notePlaceholder: "எ.கா. மாலை நேரம் விரும்புகிறேன், முன்பே அழைப்பேன்",
      sending: "அனுப்புகிறது…",
      requestVisitBtn: "வருகை கோரிக்கை அனுப்பு",
      ownerContactLabel: "உரிமையாளர்",
      verifiedOwnerBadge: "சரிபார்க்கப்பட்டது",
      unlockingBtn: "அன்லாக் ஆகிறது…",
      unlockNumberFreeBtn: "🔓 எண்ணை அன்லாக் செய் — இலவசம்",
      unlockUpsellTitle: "இலவச அன்லாக் பயன்படுத்தப்பட்டது",
      unlockUpsellBody: "பேசிக் திட்டத்தில் 1 உரிமையாளர் தொடர்பு எண் இலவசமாக அன்லாக் ஆகும். வரம்பற்ற உரிமையாளர்களை அன்லாக் செய்ய Tenant Pro-க்கு அப்கிரேட் செய்யவும்.",
      unlockUpsellBtn: "Tenant Pro-க்கு அப்கிரேட் செய்",
      callNumberBtn: "{phone} ஐ அழை",
      whatsappBtn: "💬 WhatsApp-ல் சாட் செய்",
      whatsappMessage: "வணக்கம், ToLetBook-ல் பட்டியலிடப்பட்ட உங்கள் சொத்து \"{property}\" மீது எனக்கு ஆர்வம் உள்ளது. இது இன்னும் கிடைக்குமா?",
      contactUnlockedToast: "தொடர்பு எண் அன்லாக் ஆனது",
      couldNotUnlockToast: "தொடர்பை அன்லாக் செய்ய முடியவில்லை",
    },
    tenantPayments: {
      noActiveTenancy: "வாடகை செலுத்த உங்களுக்கு செயலில் உள்ள குடியிருப்பு இல்லை.",
      title: "பணம் செலுத்துதல்",
      paidStatus: "செலுத்தப்பட்டது",
      rentDue: "வாடகை நிலுவை",
      reminderBanner: "🔔 இந்த பணம் செலுத்துதல் குறித்து உங்கள் உரிமையாளர் நினைவூட்டல் அனுப்பியுள்ளார்",
      payingBtn: "செலுத்துகிறது…",
      payViaUpi: "UPI மூலம் {amount} செலுத்து",
      payNow: "{amount} செலுத்து",
      paymentFailedToast: "பணம் செலுத்தல் தோல்வியடைந்தது — மீண்டும் முயற்சிக்கவும்",
      paymentRecordedToast: "பணம் செலுத்துதல் பதிவு செய்யப்பட்டது",
      viewReceiptBtn: "ரசீதைப் பார்",
      history: "வரலாறு",
      payViaUpiDirectlyBtn: "நேரடியாக UPI மூலம் செலுத்து",
      ownerUpiIdLabel: "உரிமையாளரின் UPI ID",
      copyUpiBtn: "காப்பி செய்",
      upiIdCopiedToast: "UPI ID காப்பி செய்யப்பட்டது",
      openUpiAppBtn: "UPI ஆப்பைத் திற",
      markPaidUpiBtn: "நான் UPI மூலம் செலுத்திவிட்டேன்",
      markPaidUpiConfirm: "இந்த வாடகையை உரிமையாளரின் UPI ID க்கு அனுப்பிவிட்டீர்களா? இது செலுத்தப்பட்டதாகக் குறிக்கப்படும்.",
    },
    tenantRequests: {
      title: "பராமரிப்பு கோரிக்கைகள்",
      cancelLink: "ரத்து செய் ›",
      newLink: "+ புதியது ›",
      whatsWrong: "என்ன பிரச்சனை?",
      issuePlaceholder: "எ.கா. குழாயில் தண்ணீர் ஒழுகுகிறது",
      detailsOptional: "விவரங்கள் (விருப்பம்)",
      submittingBtn: "சமர்ப்பிக்கிறது…",
      submitRequestBtn: "கோரிக்கையை சமர்ப்பி",
      giveTitleError: "கோரிக்கைக்கு தலைப்பு கொடுங்கள்",
      requestRaisedToast: "கோரிக்கை அனுப்பப்பட்டது",
      noRequestsYet: "இதுவரை கோரிக்கைகள் எதுவும் இல்லை.",
      noDetailsProvided: "விவரங்கள் இல்லை",
      myVisitsHeading: "எனது வருகை கோரிக்கைகள்",
      emptyNoVisits: "நீங்கள் இதுவரை எந்த சொத்து வருகை கோரிக்கையும் செய்யவில்லை.",
      visitLine: "{date} அன்று {time} மணிக்கு",
      cancelVisitButton: "ரத்து செய்",
      visitCancelledToast: "வருகை கோரிக்கை ரத்து செய்யப்பட்டது",
      visitStatusPending: "நிலுவையில்",
      visitStatusConfirmed: "உறுதி செய்யப்பட்டது",
      visitStatusDeclined: "நிராகரிக்கப்பட்டது",
      visitStatusCancelled: "ரத்து செய்யப்பட்டது",
      vacateNoticeHeading: "காலி செய்யும் அறிவிப்பு",
      giveNoticeLink: "+ அறிவிப்பு கொடு ›",
      vacateOnLabel: "{date} அன்று காலி செய்யப்படும்",
      noticeGivenOnLabel: "{date} அன்று அறிவிப்பு கொடுக்கப்பட்டது",
      vacateStatusPending: "நிலுவையில்",
      vacateStatusAcknowledged: "ஏற்றுக்கொள்ளப்பட்டது",
      vacateStatusCancelled: "ரத்து செய்யப்பட்டது",
      shareWhatsappBtn: "💬 WhatsApp-இல் பகிர்",
      cancelNoticeBtn: "அறிவிப்பை ரத்து செய்",
      noticeDateLabel: "அறிவிப்பு தேதி",
      vacateDatePreview: "உங்கள் {days}-நாள் அறிவிப்பு காலத்தின்படி, நீங்கள் {date} அன்று காலி செய்வீர்கள்",
      submitVacateNoticeBtn: "காலி செய்யும் அறிவிப்பை சமர்ப்பி",
      vacateNoticeSentToast: "காலி செய்யும் அறிவிப்பு உரிமையாளருக்கு அனுப்பப்பட்டது",
      vacateNoticeCancelledToast: "காலி செய்யும் அறிவிப்பு ரத்து செய்யப்பட்டது",
      emptyNoVacateNotices: "நீங்கள் இதுவரை எந்த காலி செய்யும் அறிவிப்பும் கொடுக்கவில்லை.",
      vacateWhatsappMessage: "வணக்கம் {owner}, நான் \"{property}\" ஐ காலி செய்யப் போவதாக முறையாக அறிவிக்க இது. அறிவிப்பு {noticeDate} அன்று கொடுக்கப்பட்டது, {vacateDate} அன்று காலி செய்வேன்.",
    },
    tenantOnboarding: {
      title: "உங்கள் குடியேற்றத்தை முடிக்கவும்",
      nameLabel: "உங்கள் பெயர் (ஆதார் படி)",
      nameHint: "இந்த சரியான பெயர் உங்கள் வாடகை ஒப்பந்தத்தில் பயன்படுத்தப்படும்.",
      rentLabel: "மாத வாடகை",
      dueDayLabel: "வாடகை செலுத்த வேண்டிய நாள்",
      dueDayValue: "ஒவ்வொரு மாதமும் {day} ஆம் தேதி",
      depositLabel: "பாதுகாப்பு டெபாசிட்",
      depositPaidBadge: "செலுத்தப்பட்டது",
      depositPendingBadge: "நிலுவை",
      depositConfirmingBadge: "உறுதிசெய்யப்படுகிறது…",
      payNowBtn: "இப்போது செலுத்து",
      payingBtn: "பணம் செலுத்துதல் திறக்கிறது…",
      depositPaymentDesc: "பாதுகாப்பு டெபாசிட்",
      depositPaidToast: "டெபாசிட் பணம் பெறப்பட்டது",
      depositFailedToast: "பணம் செலுத்தல் தோல்வியடைந்தது — மீண்டும் முயற்சிக்கவும்",
      depositPaidCashBadge: "பணமாக செலுத்தப்பட்டது",
      paidInCashLink: "நான் பணமாக செலுத்தினேன்",
      cashProofHint: "ரசீது அல்லது பணம் செலுத்திய ஆதாரப் புகைப்படத்தைச் சேர்க்கவும் (விருப்பத்தேர்வு).",
      cashProofUploadLabel: "ஆதாரம் சேர்க்க",
      confirmCashBtn: "பண செலுத்துதலை உறுதிப்படுத்தவும்",
      errDepositRequired: "தொடர்வதற்கு முன் பாதுகாப்பு டெபாசிட்டைச் செலுத்தவும் (ஆன்லைன் அல்லது பணமாக)",
      amenitiesLabel: "உரிமையாளர் வழங்கும் வசதிகள்",
      identityVerificationLabel: "அடையாள சரிபார்ப்பு",
      yourIdentityLabel: "நீங்கள்",
      identityVerificationHint: "உங்கள் அரசு-சரிபார்த்த பெயர் வாடகை ஒப்பந்தத்தில் தோன்ற DigiLocker மூலம் உங்கள் அடையாளத்தைச் சரிபார்க்கவும்.",
      confirmLabel: "மேலே உள்ள எனது குடியேற்ற விவரங்களை பார்வையிட்டேன், அவை சரியானவை என்பதை உறுதிப்படுத்துகிறேன்.",
      submitBtn: "அமைப்பை முடிக்கவும்",
      errNameRequired: "உங்கள் பெயரை உள்ளிடவும்",
      errMustConfirm: "விவரங்களை பார்வையிட்டீர்கள் என்பதை உறுதிப்படுத்தவும்",
      completedToast: "குடியேற்ற விவரங்கள் உறுதி செய்யப்பட்டன",
    },
    moveInDetails: {
      title: "குடியேற்ற விவரங்கள்",
    },
    locationCapture: {
      pinLocationLabel: "இந்த சொத்தின் இருப்பிடத்தை பின் செய்யுங்கள்",
      panMapHint: "📌 சரியான இடத்தில் பின்னை வைக்க வரைபடத்தை நகர்த்தவும்",
      expandMapAria: "வரைபடத்தை விரிவாக்கு",
      clear: "அழி",
      searchPlaceholder: "முகவரியைத் தேடுங்கள்",
      loadingSearch: "தேடல் ஏற்றுகிறது…",
      or: "அல்லது",
      locating: "இருப்பிடம் கண்டறிகிறது…",
      useCurrentLocation: "📍 தற்போதைய இருப்பிடத்தைப் பயன்படுத்து",
      pickSuggestion: "கீழ்தோன்றும் பட்டியலிலிருந்து ஒரு பரிந்துரையைத் தேர்ந்தெடுங்கள்",
      locationNotSupported: "இந்த பிரவுசரில் இருப்பிடம் ஆதரிக்கப்படவில்லை",
      locationPermissionDenied: "இருப்பிட அனுமதி மறுக்கப்பட்டது",
      locationCouldNotGet: "உங்கள் இருப்பிடத்தைப் பெற முடியவில்லை",
      hint: "முகவரியைத் தேடுங்கள் அல்லது கீழே உள்ள பகுதியை தானாக நிரப்ப GPS-ஐ தட்டுங்கள் — கதவு எண் மற்றும் கட்டிடப் பெயரை நீங்களே உள்ளிட வேண்டும்.",
    },
    googleMap: {
      yourLocation: "உங்கள் இருப்பிடம்",
      couldNotLoad: "Google Maps ஏற்ற முடியவில்லை — API கீயைச் சரிபார்க்கவும்",
      viewDetails: "விவரங்களைப் பார் ›",
    },
    rentReceipt: {
      back: "‹ பின்",
      paymentRecordedBanner: "✓ பணம் செலுத்துதல் பதிவு செய்யப்பட்டது — இதோ உங்கள் ரசீது.",
      title: "வாடகை ரசீது",
      amountPaid: "செலுத்திய தொகை",
      receiptNoLabel: "ரசீது எண்",
      paidByLabel: "செலுத்தியவர்",
      propertyLabel: "சொத்து",
      receivedByLabel: "பெற்றவர்",
      forMonthLabel: "எந்த மாதத்திற்கு",
      paymentModeLabel: "பணம் செலுத்தும் முறை",
      paidOnLabel: "செலுத்திய தேதி",
      downloadBtn: "பதிவிறக்கு",
      shareBtn: "பகிர்",
      htmlTitle: "வாடகை ரசீது - {month}",
      htmlStamp: "✓ செலுத்தப்பட்டது",
      htmlAmountPaidLabel: "செலுத்திய தொகை",
      htmlFooter: "இது ToLetBook மூலம் தானாக உருவாக்கப்பட்ட ரசீது. இங்கே காட்டப்படும் வாடகை ஆப்பிற்குள் பதிவு செய்யப்பட்டது, உண்மையான பேமெண்ட் கேட்வே மூலம் செயலாக்கப்படவில்லை.",
      shareTitle: "வாடகை ரசீது - {month}",
      shareText: "ரசீது {receiptNo}, தொகை {amount}",
    },
    rentalAgreement: {
      back: "‹ பின்",
      title: "வாடகை ஒப்பந்தம்",
      ownerLabel: "உரிமையாளர்",
      tenantLabel: "குடியிருப்பாளர்",
      propertyLabel: "சொத்து",
      typeLabel: "வகை",
      monthlyRentLabel: "மாத வாடகை",
      securityDepositLabel: "பாதுகாப்பு டெபாசிட்",
      moveInDateLabel: "குடிபெயர்ந்த தேதி",
      disclaimer: "இது பதிவில் உள்ள குடியிருப்பு விவரங்களிலிருந்து தானாக உருவாக்கப்பட்ட சுருக்கம், சட்டப்பூர்வமாக கட்டுப்படுத்தும் ஆவணம் அல்ல. இயல்பான டெமோ நிபந்தனைகள்: ஒவ்வொரு மாதமும் 5ஆம் தேதிக்குள் வாடகை செலுத்த வேண்டும், குடியிருப்பை முடிக்க 30 நாட்கள் அறிவிப்பு, சாதாரண தேய்மானத்தை தாண்டிய சேதங்களுக்கு கழிவு செய்து டெபாசிட் திரும்பப் பெறலாம்.",
      downloadAgreementBtn: "ஒப்பந்தத்தை பதிவிறக்கு",
      stampStatusLabel: "E-Stamp நிலை",
      stampStatusNotStarted: "தொடங்கவில்லை",
      stampStatusProcessing: "செயலாக்கத்தில் உள்ளது",
      stampStatusVerified: "சரிபார்க்கப்பட்டது",
      stampHint: "இந்த ஒப்பந்தத்தை அரசாங்கத்திடமிருந்து சட்டப்பூர்வமாக E-Stamp செய்யுங்கள். இதற்கு {amount} உண்மையான அரசு கட்டணம் பொருந்தும்.",
      stampFeeAlreadyPaidHint: "உங்கள் E-Stamp கட்டணம் செலுத்தப்பட்டது — ஆர்டரை மட்டும் இன்னும் வைக்க முடியவில்லை. கூடுதல் கட்டணம் இல்லாமல் கீழே மீண்டும் முயற்சிக்கவும்.",
      sendForStampBtn: "{amount} செலுத்தி E-Stamp க்கு அனுப்பு",
      retrySendForStampBtn: "E-Stamp க்கு மீண்டும் அனுப்பு",
      sendingStampBtn: "அனுப்புகிறது…",
      confirmingPaymentBtn: "கட்டணம் உறுதிசெய்யப்படுகிறது…",
      stampFeePaymentDesc: "அரசு E-Stamp கட்டணம்",
      stampFeeFailedToast: "கட்டணம் தோல்வியடைந்தது — மீண்டும் முயற்சிக்கவும்",
      stampSentToast: "அரசு E-Stamping க்கு அனுப்பப்பட்டது",
      refreshStatusBtn: "நிலையை புதுப்பிக்கவும்",
      viewStampedBtn: "ஸ்டாம்ப் செய்யப்பட்ட ஒப்பந்தத்தைப் பார்",
      identityVerificationLabel: "அடையாள சரிபார்ப்பு",
      digilockerVerifiedBadge: "சரிபார்க்கப்பட்டது",
      digilockerNotVerifiedBadge: "சரிபார்க்கப்படவில்லை",
      verifyDigilockerBtn: "DigiLocker மூலம் சரிபார்க்கவும்",
      verifyingDigilockerBtn: "DigiLocker திறக்கிறது…",
      digilockerVerifiedToast: "{name} என சரிபார்க்கப்பட்டது",
      digilockerDeniedToast: "DigiLocker சரிபார்ப்பு ரத்து செய்யப்பட்டது",
      digilockerExpiredToast: "அந்த DigiLocker இணைப்பு காலாவதியானது — மீண்டும் முயற்சிக்கவும்",
      comingSoonTitle: "அரசு இ-ஸ்டாம்பிங் விரைவில் வருகிறது",
      comingSoonBody: "நீங்கள் ஆப்பிலிருந்தே முழுமையாக அரசு-சரிபார்க்கப்பட்ட, இ-ஸ்டாம்ப் செய்யப்பட்ட வாடகை ஒப்பந்தத்தை உருவாக்கும் வகையில் நாங்கள் ஸ்டாம்ப் டியூட்டி பணம் செலுத்தலை அமைத்து வருகிறோம்.",
      interestBtn: "எனக்கு ஆர்வம் உள்ளது — அறிவிக்கவும்",
      interestRegisteredBtn: "நீங்கள் பட்டியலில் உள்ளீர்கள் ✓",
      interestToast: "நன்றி! இ-ஸ்டாம்பிங் லைவ் ஆனதும் நாங்கள் உங்களுக்கு அறிவிப்போம்.",
      uploadDocHeading: "ஏற்கனவே வாடகை ஒப்பந்தம் உள்ளதா?",
      uploadDocHint: "நீங்கள் ToLetBook க்கு வெளியே ஏற்கனவே ஒன்றை உருவாக்கியிருந்தால், இந்த குடியிருப்புடன் சேமிக்க இங்கே பதிவேற்றவும்.",
      uploadDocBtn: "வாடகை ஒப்பந்தத்தை பதிவேற்றவும்",
      viewUploadedDocBtn: "பதிவேற்றிய ஒப்பந்தத்தைப் பார்க்கவும்",
      removeUploadedDocBtn: "அகற்று",
      uploadedOnLabel: "{date} அன்று பதிவேற்றப்பட்டது",
      noUploadedDocTenantHint: "உங்கள் உரிமையாளர் இன்னும் வாடகை ஒப்பந்தத்தைப் பதிவேற்றவில்லை.",
      uploadDocSuccessToast: "வாடகை ஒப்பந்தம் பதிவேற்றப்பட்டது",
      uploadDocFailedToast: "பதிவேற்ற முடியவில்லை — 8MB க்கும் குறைவான PDF அல்லது படத்தை முயற்சிக்கவும்",
      removeDocConfirm: "பதிவேற்றிய வாடகை ஒப்பந்தத்தை அகற்றவா?",
      removeDocSuccessToast: "வாடகை ஒப்பந்தம் அகற்றப்பட்டது",
      htmlTitle: "வாடகை ஒப்பந்தம் - {property}",
      htmlDocTitle: "வாடகை ஒப்பந்த சுருக்கம்",
      htmlAgreementNo: "ஒப்பந்த எண். {no}",
      htmlOwnerLessor: "உரிமையாளர் / குத்தகைதாரர்",
      htmlTenantLessee: "குடியிருப்பாளர் / குத்தகைக்காரர்",
      htmlPropertyHeading: "சொத்து",
      htmlAddressLabel: "முகவரி",
      htmlTypeLabel: "வகை",
      htmlTermsHeading: "நிபந்தனைகள்",
      htmlMonthlyRentLabel: "மாத வாடகை",
      htmlSecurityDepositLabel: "பாதுகாப்பு டெபாசிட்",
      htmlMoveInDateLabel: "குடிபெயர்ந்த தேதி",
      htmlConditionsHeading: "நிபந்தனைகள்",
      htmlCond1: "வாடகை ஒவ்வொரு மாதமும் 5ஆம் தேதிக்குள், ஒப்புக்கொண்ட முறையில் உரிமையாளருக்கு செலுத்தப்பட வேண்டும்.",
      htmlCond2: "சாதாரண தேய்மானத்தை தாண்டிய சேதங்களுக்கு கழிவு செய்து, வெளியேறும் போது பாதுகாப்பு டெபாசிட் திரும்பச் செலுத்தப்படும்.",
      htmlCond3: "குடியிருப்பை முடிக்கு முன் இரு தரப்பினரும் குறைந்தது 30 நாட்கள் எழுத்துப்பூர்வ அறிவிப்பு கொடுக்க வேண்டும்.",
      htmlCond4: "உரிமையாளருடன் வேறு விதமாக ஒப்புக்கொள்ளாவிட்டால், பயன்பாட்டு பில்களுக்கு குடியிருப்பாளரே பொறுப்பு.",
      htmlCond5: "குடியிருப்பாளரின் அசமந்தத்தால் சேதம் ஏற்படாவிட்டால், பொருத்துகைகள் மற்றும் அமைப்புகளின் பராமரிப்புக்கு உரிமையாளரே பொறுப்பு.",
      htmlFooter: "இந்த சுருக்கம் பதிவில் உள்ள குடியிருப்பு விவரங்களிலிருந்து ToLetBook மூலம் தானாக உருவாக்கப்பட்டது, இது சட்டப்பூர்வமாக கட்டுப்படுத்தும் ஆவணம் அல்ல. முறையான வாடகை ஒப்பந்தத்தை தயாரிக்க ஒரு சட்ட நிபுணரை அணுகவும்.",
    },
    photoLightbox: {
      closeAria: "மூடு",
      previousPhotoAria: "முந்தைய புகைப்படம்",
      nextPhotoAria: "அடுத்த புகைப்படம்",
      countLabel: "{current} / {total}",
    },
    tenantPlan: {
      title: "திட்டம்",
      currentPlanLabel: "தற்போதைய திட்டம்",
      basicName: "பேசிக்",
      proName: "குடியிருப்பாளர் புரோ",
      proSub: "தீவிரமாக வீடு தேடும் குடியிருப்பாளர்களுக்கு",
      priceValidity: "1 மாதம் செல்லுபடியாகும்",
      featUnlock: "30 நாட்களுக்கு வரம்பற்ற உரிமையாளர் தொடர்பு எண்களை அன்லாக் செய்யுங்கள்",
      featNearby: "ஒவ்வொரு பட்டியலிலும் முழு வரைபடம் மற்றும் அருகிலுள்ள இடங்களுக்கான அணுகல்",
      featWhatsapp: "உரிமையாளரிடம் நேரடியாக WhatsApp-இல் பேசுங்கள், ஒரு பட்டியலுக்கு கட்டணம் இல்லை",
      featFallback: "சப்ஸ்கிரைப் செய்யாவிட்டாலும் 1 உரிமையாளர் தொடர்பு இலவசமாக அன்லாக் ஆகும் — பிறகு அப்கிரேட் செய்ய வேண்டும்",
      currentPlanBtn: "தற்போதைய திட்டம்",
      subscribeBtn: "₹99க்கு சப்ஸ்கிரைப் செய்யுங்கள்",
      activeUntil: "{date} வரை செயலில்",
      noChargeNote: "இன்னும் பணம் செலுத்த வேண்டியதில்லை — பில்லிங் அமைக்கப்படும் வரை சப்ஸ்கிரைப் செய்வது இலவசம்.",
      subscribedToast: "குடியிருப்பாளர் புரோ செயல்படுத்தப்பட்டது",
      switchFailedToast: "உங்கள் திட்டத்தை புதுப்பிக்க முடியவில்லை",
      nearbyUpsellTitle: "வரைபடம் மற்றும் அருகிலுள்ள இடங்களை அன்லாக் செய்யுங்கள்",
      nearbyUpsellBody: "ஒவ்வொரு பட்டியலுக்கு அருகிலும் போக்குவரத்து, அத்தியாவசியங்கள் மற்றும் சுகாதாரத்தைப் பார்க்க குடியிருப்பாளர் புரோவுக்கு சப்ஸ்கிரைப் செய்யுங்கள்.",
      nearbyUpsellBtn: "குடியிருப்பாளர் புரோவைப் பார்",
      cancelPlanBtn: "திட்டத்தை ரத்து செய்",
      securePaymentNote: "பணம் செலுத்துதல் Razorpay மூலம் பாதுகாப்பாக செயலாக்கப்படுகிறது.",
      cancelledToast: "குடியிருப்பாளர் புரோ ரத்து செய்யப்பட்டது",
    },
  },
};
